import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Icon } from "../components/Icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import TargetsAppraisalsView from "../components/TargetsAppraisalsView";
import "../styles/EmployerHome.css";

const token = () => sessionStorage.getItem("jwtToken");
const hdr = () => ({ Authorization: `Bearer ${token()}` });
const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const getGreeting = () => { const h = new Date().getHours(); return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening"; };

const getSunSat = (base = new Date()) => {
  const d = new Date(base); d.setHours(0, 0, 0, 0);
  const sun = new Date(d); sun.setDate(d.getDate() - d.getDay());
  const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
  return { weekStart: fmt(sun), weekEnd: fmt(sat) };
};

const getTimesheetHoursInRange = (timesheet, fromDate, toDate) => {
  if (!timesheet) return 0;
  const start = dayjs(timesheet.week_start);
  const daysOfWeek = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  let sum = 0;
  daysOfWeek.forEach((day, index) => {
    const dayDate = start.add(index, "day");
    const dateStr = dayDate.format("YYYY-MM-DD");
    if ((!fromDate || dateStr >= fromDate) && (!toDate || dateStr <= toDate)) {
      sum += Number(timesheet.daily_hours?.[day] || 0);
    }
  });
  return sum;
};

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: "Home" },
  { key: "clients", label: "Clients", icon: "Briefcase" },
  { key: "employees", label: "Employees", icon: "Users" },
  { key: "leaves", label: "Leave Administration", icon: "Calendar" },
  { key: "approvals", label: "Timesheet Management", icon: "CheckCircle" },
  { key: "appraisals", label: "Targets & Appraisals", icon: "Target" },
  { key: "holidays", label: "Holiday Administration", icon: "Grid" },
  { key: "reports", label: "Reports", icon: "BarChart" },
  { key: "feedback", label: "Feedback", icon: "MessageSquare" },
  // { key: "settings", label: "Settings", icon: "Badge" },
];

const CircProg = ({ pct, size = 80, stroke = 7 }) => {
  const r = Math.floor((size - stroke * 2) / 2), cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r, dash = (pct / 100) * circ;
  return (
    <div className="em-progress-circle" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2563EB" strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div className="em-progress-circle-text">{pct}%</div>
    </div>
  );
};

const isAmericaTz = (tz) => {
  const t = (tz || "").toLowerCase();
  return t.startsWith("america/") || t.includes("york") || t.includes("chicago") || t.includes("denver") || t.includes("los_angeles") || t.includes("est") || t.includes("edt") || t.includes("cst") || t.includes("pst") || t.includes("us") || t.includes("america");
};

const TIMEZONE_OPTIONS = [
  { value: "Asia/Kolkata", label: "Asia/Kolkata" },
  { value: "America/New_York", label: "America/New_York" },
];

/* ── Holiday Administration view ──
   Lets the employer select dates on a calendar and assign them as holidays
   separately for Indian (Asia/Kolkata) and US (America/New_York) employees.
   Supports Excel batch import for a full year with fields: Month, Date, Day, Holiday Name, Holiday Type.
   Assigned holidays show up as "Holiday" on the matching employees' timesheets. */
const HOLIDAY_REGIONS = [
  { key: "IN", label: "India", timezone: "Asia/Kolkata" },
  { key: "US", label: "United States", timezone: "America/New_York" },
];

const parseMonthNumber = (monthStr) => {
  if (monthStr === undefined || monthStr === null) return null;
  const s = String(monthStr).trim().toLowerCase();
  if (!s) return null;
  const num = parseInt(s, 10);
  if (!isNaN(num) && num >= 1 && num <= 12) return num;
  const months = {
    jan: 1, january: 1,
    feb: 2, february: 2,
    mar: 3, march: 3,
    apr: 4, april: 4,
    may: 5,
    jun: 6, june: 6,
    jul: 7, july: 7,
    aug: 8, august: 8,
    sep: 9, sept: 9, september: 9,
    oct: 10, october: 10,
    nov: 11, november: 11,
    dec: 12, december: 12,
  };
  return months[s] || null;
};

const parseDateAndResolve = (dateVal, monthVal, yearVal) => {
  if (dateVal === undefined || dateVal === null) return null;

  // 1. If it's an Excel numeric date code (serial date > 1000)
  if (typeof dateVal === "number" && dateVal > 1000 && typeof XLSX !== "undefined" && XLSX.SSF) {
    try {
      const parsed = XLSX.SSF.parse_date_code(dateVal);
      if (parsed && parsed.y && parsed.m && parsed.d) {
        return dayjs(`${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`);
      }
    } catch {}
  }

  const dateStr = String(dateVal).trim();
  if (!dateStr) return null;

  // 2. If it's already a full date string like YYYY-MM-DD or DD/MM/YYYY
  if (dateStr.includes("-") || dateStr.includes("/") || dateStr.includes(" ")) {
    let parsed = dayjs(dateStr);
    if (parsed.isValid() && parsed.year() > 1990) {
      return parsed;
    }
    const parts = dateStr.split(/[-/]/);
    if (parts.length === 3) {
      const p0 = parseInt(parts[0], 10);
      const p1 = parseInt(parts[1], 10);
      const p2 = parseInt(parts[2], 10);
      if (p2 > 1900 && p0 <= 31 && p1 <= 12) {
        return dayjs(`${p2}-${String(p1).padStart(2, "0")}-${String(p0).padStart(2, "0")}`);
      }
    }
  }

  // 3. If dateVal is just a day-of-month number (e.g. 26 or "26")
  const dayNum = parseInt(dateStr, 10);
  const monthNum = parseMonthNumber(monthVal);
  const yearNum = parseInt(yearVal, 10) || dayjs().year();

  if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31 && monthNum) {
    const formatted = `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    const d = dayjs(formatted);
    if (d.isValid()) return d;
  }

  return null;
};

const downloadHolidayTemplate = (regionKey, targetYear) => {
  const isIndia = regionKey === "IN";
  const sampleData = isIndia
    ? [
        { "Month": "January", "Date": 26, "Day": "Monday", "Holiday Name": "Republic Day", "Holiday Type": "Gazetted" },
        { "Month": "March", "Date": 25, "Day": "Wednesday", "Holiday Name": "Holi", "Holiday Type": "Gazetted" },
        { "Month": "April", "Date": 10, "Day": "Friday", "Holiday Name": "Good Friday", "Holiday Type": "Gazetted" },
        { "Month": "May", "Date": 1, "Day": "Friday", "Holiday Name": "May Day", "Holiday Type": "Public" },
        { "Month": "August", "Date": 15, "Day": "Saturday", "Holiday Name": "Independence Day", "Holiday Type": "National" },
        { "Month": "October", "Date": 2, "Day": "Friday", "Holiday Name": "Mahatma Gandhi Jayanti", "Holiday Type": "Gazetted" },
        { "Month": "October", "Date": 20, "Day": "Tuesday", "Holiday Name": "Dussehra", "Holiday Type": "Gazetted" },
        { "Month": "November", "Date": 8, "Day": "Sunday", "Holiday Name": "Diwali", "Holiday Type": "Gazetted" },
        { "Month": "December", "Date": 25, "Day": "Friday", "Holiday Name": "Christmas Day", "Holiday Type": "Gazetted" },
      ]
    : [
        { "Month": "January", "Date": 1, "Day": "Thursday", "Holiday Name": "New Year's Day", "Holiday Type": "Federal" },
        { "Month": "January", "Date": 19, "Day": "Monday", "Holiday Name": "Martin Luther King Jr. Day", "Holiday Type": "Federal" },
        { "Month": "February", "Date": 16, "Day": "Monday", "Holiday Name": "Presidents' Day", "Holiday Type": "Federal" },
        { "Month": "May", "Date": 25, "Day": "Monday", "Holiday Name": "Memorial Day", "Holiday Type": "Federal" },
        { "Month": "June", "Date": 19, "Day": "Friday", "Holiday Name": "Juneteenth", "Holiday Type": "Federal" },
        { "Month": "July", "Date": 4, "Day": "Saturday", "Holiday Name": "Independence Day", "Holiday Type": "Federal" },
        { "Month": "September", "Date": 7, "Day": "Monday", "Holiday Name": "Labor Day", "Holiday Type": "Federal" },
        { "Month": "October", "Date": 12, "Day": "Monday", "Holiday Name": "Columbus Day", "Holiday Type": "Federal" },
        { "Month": "November", "Date": 11, "Day": "Wednesday", "Holiday Name": "Veterans Day", "Holiday Type": "Federal" },
        { "Month": "November", "Date": 26, "Day": "Thursday", "Holiday Name": "Thanksgiving Day", "Holiday Type": "Federal" },
        { "Month": "December", "Date": 25, "Day": "Friday", "Holiday Name": "Christmas Day", "Holiday Type": "Federal" },
      ];

  const ws = XLSX.utils.json_to_sheet(sampleData, {
    header: ["Month", "Date", "Day", "Holiday Name", "Holiday Type"],
  });
  ws["!cols"] = [
    { wch: 14 },
    { wch: 10 },
    { wch: 14 },
    { wch: 30 },
    { wch: 18 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Holidays");
  XLSX.writeFile(wb, `holiday_template_${regionKey}_${targetYear}.xlsx`);
};

const HolidayAdminView = () => {
  const [region, setRegion] = useState("IN");
  const [viewMonth, setViewMonth] = useState(dayjs().startOf("month"));
  const [holidays, setHolidays] = useState([]); // list of { id, date, name, timezone, holidayType }
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [typeDraft, setTypeDraft] = useState("Gazetted");
  const [selectedDate, setSelectedDate] = useState(null);
  const [message, setMessage] = useState(null);

  // Excel Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importRegion, setImportRegion] = useState("IN");
  const [importYear, setImportYear] = useState(dayjs().year());
  const [importFileName, setImportFileName] = useState("");
  const [parsedHolidays, setParsedHolidays] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const activeRegion = HOLIDAY_REGIONS.find(r => r.key === region);
  const timezone = activeRegion.timezone;

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/timesheets/holidays/region?timezone=${encodeURIComponent(timezone)}`, { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        setHolidays(Array.isArray(data) ? data : []);
      } else {
        setHolidays([]);
      }
    } catch (e) { console.error(e); setHolidays([]); }
    finally { setLoading(false); }
  }, [timezone]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const holidayMap = useMemo(() => {
    const map = {};
    holidays.forEach(h => { if (h.date) map[h.date] = h; });
    return map;
  }, [holidays]);

  const daysInMonth = viewMonth.daysInMonth();
  const firstDay = viewMonth.startOf("month").day();
  const cells = Array.from({ length: firstDay + daysInMonth }, (_, i) =>
    i < firstDay ? null : viewMonth.date(i - firstDay + 1)
  );

  const assignHoliday = async (dateStr, name, holidayType) => {
    setSavingDate(dateStr);
    setMessage(null);
    try {
      const res = await fetch("/timesheets/holidays/assign", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...hdr() },
        body: JSON.stringify({
          date: dateStr,
          timezone,
          name: name || "Holiday",
          holidayType: holidayType || (region === "IN" ? "Gazetted" : "Federal"),
        }),
      });
      if (res.ok) {
        await fetchHolidays();
        setMessage({ type: "success", text: `Holiday assigned for ${activeRegion.label} employees on ${dayjs(dateStr).format("MMM D, YYYY")}.` });
      } else {
        setMessage({ type: "error", text: "Failed to assign holiday." });
      }
    } catch (e) { console.error(e); setMessage({ type: "error", text: "Error assigning holiday." }); }
    finally {
      setSavingDate(null);
      setSelectedDate(null);
      setNameDraft("");
      setTypeDraft(region === "IN" ? "Gazetted" : "Federal");
    }
  };

  const removeHoliday = async (dateStr) => {
    setSavingDate(dateStr);
    setMessage(null);
    try {
      const res = await fetch(`/timesheets/holidays?date=${encodeURIComponent(dateStr)}&timezone=${encodeURIComponent(timezone)}`, {
        method: "DELETE",
        headers: hdr(),
      });
      if (res.ok) {
        await fetchHolidays();
        setMessage({ type: "success", text: `Holiday removed for ${activeRegion.label} employees on ${dayjs(dateStr).format("MMM D, YYYY")}.` });
      } else {
        setMessage({ type: "error", text: "Failed to remove holiday." });
      }
    } catch (e) { console.error(e); setMessage({ type: "error", text: "Error removing holiday." }); }
    finally { setSavingDate(null); setSelectedDate(null); setNameDraft(""); }
  };

  const handleDayClick = (d) => {
    if (!d) return;
    const dateStr = d.format("YYYY-MM-DD");
    if (holidayMap[dateStr]) {
      // Toggle off an existing holiday
      removeHoliday(dateStr);
    } else {
      // Open an inline name prompt before assigning
      setSelectedDate(dateStr);
      setNameDraft("Holiday");
      setTypeDraft(region === "IN" ? "Gazetted" : "Federal");
    }
  };

  // Process uploaded Excel / CSV file
  const processUploadedFile = (file) => {
    if (!file) return;
    setImportFileName(file.name);
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array" });
        if (!wb.SheetNames || wb.SheetNames.length === 0) {
          setImportError("The uploaded Excel workbook contains no sheets.");
          setParsedHolidays([]);
          return;
        }
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!json || json.length === 0) {
          setImportError("The uploaded spreadsheet contains no data rows.");
          setParsedHolidays([]);
          return;
        }

        const rows = [];
        json.forEach((rawRow, idx) => {
          const keys = Object.keys(rawRow);
          const findKey = (pattern) => keys.find(k => pattern.test(String(k).trim()));

          const mKey = findKey(/^month$/i) || findKey(/month/i);
          const dKey = findKey(/^date$/i) || findKey(/^day date$/i) || findKey(/date/i);
          const dayKey = findKey(/^day$/i) || findKey(/^day of week$/i) || findKey(/^weekday$/i);
          const nameKey = findKey(/^holiday name$/i) || findKey(/^holiday_name$/i) || findKey(/^name$/i) || findKey(/^holiday$/i);
          const typeKey = findKey(/^holiday type$/i) || findKey(/^holiday_type$/i) || findKey(/^type$/i);

          const rawMonth = mKey ? rawRow[mKey] : "";
          const rawDate = dKey ? rawRow[dKey] : "";
          const rawDay = dayKey ? rawRow[dayKey] : "";
          const rawName = nameKey ? rawRow[nameKey] : "";
          const rawType = typeKey ? rawRow[typeKey] : "";

          // Skip completely empty row
          if (!rawMonth && !rawDate && !rawName && !rawType) return;

          const resolved = parseDateAndResolve(rawDate, rawMonth, importYear);
          const resolvedDateStr = resolved && resolved.isValid() ? resolved.format("YYYY-MM-DD") : null;
          const computedDay = resolved && resolved.isValid() ? resolved.format("dddd") : "";
          const effectiveDay = (rawDay ? String(rawDay).trim() : "") || computedDay;
          const effectiveName = (rawName ? String(rawName).trim() : "") || "Holiday";
          const effectiveType = (rawType ? String(rawType).trim() : "") || (importRegion === "IN" ? "Gazetted" : "Federal");

          const isValid = !!resolvedDateStr;
          rows.push({
            rowNum: idx + 2,
            rawMonth: String(rawMonth || (resolved ? resolved.format("MMMM") : "")),
            rawDate: String(rawDate || (resolved ? resolved.date() : "")),
            rawDay: effectiveDay,
            holidayName: effectiveName,
            holidayType: effectiveType,
            resolvedDate: resolvedDateStr,
            isValid,
            errorMsg: isValid ? null : "Invalid month or date format",
          });
        });

        if (rows.length === 0) {
          setImportError("No holiday records found in the sheet. Please ensure columns match: Month, Date, Day, Holiday Name, Holiday Type.");
        }
        setParsedHolidays(rows);
      } catch (err) {
        console.error(err);
        setImportError("Failed to read Excel file: " + err.message);
        setParsedHolidays([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExecuteBatchImport = async () => {
    const validItems = parsedHolidays.filter(r => r.isValid && r.resolvedDate);
    if (validItems.length === 0) {
      setImportError("No valid holiday rows to import.");
      return;
    }
    setIsImporting(true);
    setImportError(null);
    try {
      const targetTz = HOLIDAY_REGIONS.find(r => r.key === importRegion)?.timezone || "Asia/Kolkata";
      const targetLabel = HOLIDAY_REGIONS.find(r => r.key === importRegion)?.label || "Employees";

      const payload = {
        timezone: targetTz,
        holidays: validItems.map(r => ({
          date: r.resolvedDate,
          name: r.holidayName,
          holidayType: r.holidayType,
        })),
      };

      const res = await fetch("/timesheets/holidays/batch-import", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...hdr() },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const respData = await res.json();
        // If current region matches imported region, refresh; otherwise switch to imported region
        if (region !== importRegion) {
          setRegion(importRegion);
        } else {
          await fetchHolidays();
        }

        // Navigate calendar to the first imported holiday or start of import year
        if (validItems.length > 0 && validItems[0].resolvedDate) {
          setViewMonth(dayjs(validItems[0].resolvedDate).startOf("month"));
        } else {
          setViewMonth(dayjs(`${importYear}-01-01`));
        }

        setMessage({
          type: "success",
          text: `Successfully imported ${respData.count || validItems.length} holidays for ${targetLabel} (${importYear})! Employees will see them on their timesheets.`
        });
        setShowImportModal(false);
        setParsedHolidays([]);
        setImportFileName("");
      } else {
        const errMsg = await res.text();
        setImportError("Import failed: " + errMsg);
      }
    } catch (e) {
      console.error(e);
      setImportError("Error executing import: " + e.message);
    } finally {
      setIsImporting(false);
    }
  };

  const upcoming = holidays
    .slice()
    .filter(h => h.date)
    .sort((a, b) => a.date.localeCompare(b.date));

  const validCount = parsedHolidays.filter(r => r.isValid).length;
  const invalidCount = parsedHolidays.length - validCount;

  return (
    <div className="em-card" style={{ padding: 0 }}>
      <div className="em-card-header" style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "18px 22px",
      }}>
        <div>
          <h3 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Holiday Administration</h3>
        </div>

        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
          marginLeft: "auto",
          whiteSpace: "nowrap",
        }}>
          {/* Import Excel Button */}
          <button
            onClick={() => {
              setImportRegion(region);
              setImportYear(viewMonth.year());
              setParsedHolidays([]);
              setImportFileName("");
              setImportError(null);
              setShowImportModal(true);
            }}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 14px", borderRadius: 8,
              border: "1px solid #10B981", background: "#ECFDF5", color: "#047857",
              fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#D1FAE5"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#ECFDF5"; }}
            title="Import holidays for a full year from an Excel file (.xlsx)"
          >
            <Icon.Upload size={14} color="#047857" />
            Import Excel
          </button>

          {/* Region toggle */}
          <div style={{ display: "flex", gap: 4, background: "#F1F5F9", padding: 3, borderRadius: 10 }}>
            {HOLIDAY_REGIONS.map(r => (
              <button
                key={r.key}
                onClick={() => { setRegion(r.key); setSelectedDate(null); setNameDraft(""); setMessage(null); }}
                style={{
                  padding: "6px 14px", borderRadius: 7, border: "none", cursor: "pointer",
                  fontSize: "0.78rem", fontWeight: 700,
                  background: region === r.key ? "#2563EB" : "transparent",
                  color: region === r.key ? "#fff" : "#475569",
                  transition: "background 0.15s ease",
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {message && (
        <div style={{
          margin: "0 22px 14px", padding: "9px 16px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600,
          background: message.type === "success" ? "#ECFDF5" : "#FEF2F2",
          color: message.type === "success" ? "#059669" : "#DC2626",
          border: `1px solid ${message.type === "success" ? "#A7F3D0" : "#FECACA"}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontWeight: 700 }}>✕</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", padding: "0 22px 22px" }}>
        {/* Calendar */}
        <div style={{ flex: "1 1 420px", minWidth: 320 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <h4 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A" }}>{viewMonth.format("MMMM YYYY")}</h4>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="em-action-btn view" style={{ padding: "4px 10px" }} onClick={() => setViewMonth(v => v.subtract(1, "month"))}>‹</button>
              <button className="em-action-btn view" style={{ padding: "4px 10px" }} onClick={() => setViewMonth(v => v.add(1, "month"))}>›</button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", padding: "4px 0" }}>{d}</div>
            ))}
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const dateStr = d.format("YYYY-MM-DD");
              const isHoliday = !!holidayMap[dateStr];
              const isToday = d.isSame(dayjs(), "day");
              const isSaving = savingDate === dateStr;
              const isSelected = selectedDate === dateStr;
              const holidayObj = holidayMap[dateStr];
              const tooltip = isHoliday
                ? `${holidayObj.name || "Holiday"}${holidayObj.holidayType ? ` (${holidayObj.holidayType})` : ""} - Click to remove`
                : "Click to assign holiday";
              return (
                <button
                  key={i}
                  title={tooltip}
                  onClick={() => handleDayClick(d)}
                  disabled={isSaving}
                  style={{
                    textAlign: "center", padding: "8px 2px", borderRadius: 8, cursor: "pointer",
                    fontSize: "0.78rem", fontWeight: isHoliday || isToday ? 700 : 500,
                    color: isHoliday ? "#B45309" : (isToday ? "#2563EB" : "#475569"),
                    background: isHoliday ? "#FEF3C7" : (isSelected ? "#DBEAFE" : "transparent"),
                    border: isHoliday ? "1px solid #FCD34D" : (isToday ? "1px solid #2563EB" : "1px solid transparent"),
                    opacity: isSaving ? 0.5 : 1,
                  }}
                >
                  {d.date()}
                </button>
              );
            })}
          </div>

          {/* Inline prompt for assigning holiday */}
          {selectedDate && (
            <div style={{ marginTop: 14, padding: "14px 16px", background: "#F8FBFF", border: "1px solid #DBEAFE", borderRadius: 10 }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#1E293B", marginBottom: 10 }}>
                Assign holiday on {dayjs(selectedDate).format("MMM D, YYYY")} for {activeRegion.label}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  type="text"
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  placeholder="Holiday name (e.g. Diwali)"
                  style={{ flex: "1 1 180px", minWidth: 140, padding: "7px 10px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.82rem", outline: "none" }}
                  autoFocus
                />
                <select
                  value={typeDraft}
                  onChange={e => setTypeDraft(e.target.value)}
                  style={{ padding: "7px 10px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.82rem", background: "#fff", outline: "none" }}
                >
                  {(region === "IN"
                    ? ["Gazetted", "Restricted", "National", "Public", "Optional"]
                    : ["Federal", "State", "Public", "Company", "Optional"]
                  ).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <button
                  onClick={() => assignHoliday(selectedDate, nameDraft, typeDraft)}
                  disabled={savingDate === selectedDate}
                  style={{ padding: "7px 16px", background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}
                >
                  Assign
                </button>
                <button
                  onClick={() => { setSelectedDate(null); setNameDraft(""); }}
                  style={{ padding: "7px 12px", background: "#94A3B8", color: "#fff", border: "none", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16, fontSize: "0.72rem" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 8px", background: "#FEF3C7", borderRadius: 999, color: "#B45309", fontWeight: 600 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#B45309" }} /> Holiday
            </span>
            <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>Click a day to assign; click an existing holiday to remove.</span>
          </div>
        </div>

        {/* Assigned holidays list */}
        <div style={{ flex: "1 1 300px", minWidth: 260 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <h4 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A" }}>
              {activeRegion.label} Holidays
            </h4>
            <span style={{ fontSize: "0.74rem", fontWeight: 700, color: "#64748B", background: "#F1F5F9", padding: "2px 8px", borderRadius: 999 }}>
              {upcoming.length} assigned
            </span>
          </div>
          {loading ? (
            <div className="em-empty"><p>Loading...</p></div>
          ) : upcoming.length === 0 ? (
            <div className="em-empty" style={{ padding: "24px 12px" }}>
              <Icon.Calendar size={26} color="#CBD5E1" />
              <p>No holidays assigned for {activeRegion.label} employees yet.</p>
              <button
                onClick={() => {
                  setImportRegion(region);
                  setImportYear(viewMonth.year());
                  setShowImportModal(true);
                }}
                style={{ marginTop: 10, padding: "6px 14px", background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, fontSize: "0.76rem", fontWeight: 700, cursor: "pointer" }}
              >
                Import from Excel
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 440, overflowY: "auto" }}>
              {upcoming.map(h => (
                <div key={h.id || h.date} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#92400E" }}>{h.name || "Holiday"}</span>
                      {(h.holidayType || h.type) && (
                        <span style={{
                          fontSize: "0.66rem", fontWeight: 700, padding: "1px 7px", borderRadius: 999,
                          background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D",
                        }}>
                          {h.holidayType || h.type}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "0.74rem", color: "#B45309", marginTop: 2 }}>
                      {dayjs(h.date).format("ddd, MMM D, YYYY")}
                    </div>
                  </div>
                  <button
                    onClick={() => removeHoliday(h.date)}
                    disabled={savingDate === h.date}
                    title="Remove holiday"
                    style={{ padding: "5px 10px", background: "#EF4444", color: "#fff", border: "none", borderRadius: 7, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── IMPORT EXCEL MODAL ── */}
      {showImportModal && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(15, 23, 42, 0.65)",
          backdropFilter: "blur(4px)",
          zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16,
        }}>
          <div style={{
            background: "#ffffff",
            borderRadius: 16,
            width: "100%",
            maxWidth: 780,
            maxHeight: "92vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
            animation: "modalFadeIn 0.2s ease-out",
          }}>
            {/* Modal Header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "18px 24px", borderBottom: "1px solid #E2E8F0", background: "#F8FAFC",
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center", color: "#059669" }}>
                    <Icon.Upload size={18} color="#059669" />
                  </div>
                  <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>
                    Import Holidays for a Year
                  </h3>
                </div>
                <p style={{ fontSize: "0.78rem", color: "#64748B", margin: "4px 0 0 40px" }}>
                  Upload an Excel spreadsheet with columns: <b>Month</b>, <b>Date</b>, <b>Day</b>, <b>Holiday Name</b>, and <b>Holiday Type</b>.
                </p>
              </div>
              <button
                onClick={() => { setShowImportModal(false); setParsedHolidays([]); setImportFileName(""); setImportError(null); }}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748B", padding: 6, borderRadius: 6 }}
                title="Close"
              >
                <Icon.X size={18} color="#64748B" />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Year & Region Configuration Bar */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap",
                gap: 12, padding: "12px 16px", background: "#F1F5F9", borderRadius: 10,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  {/* Region selection */}
                  <div>
                    <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 4 }}>
                      Target Region
                    </label>
                    <div style={{ display: "flex", gap: 4 }}>
                      {HOLIDAY_REGIONS.map(r => (
                        <button
                          key={r.key}
                          type="button"
                          onClick={() => setImportRegion(r.key)}
                          style={{
                            padding: "4px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                            fontSize: "0.76rem", fontWeight: 700,
                            background: importRegion === r.key ? "#2563EB" : "#E2E8F0",
                            color: importRegion === r.key ? "#fff" : "#475569",
                          }}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Year selection */}
                  <div>
                    <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 4 }}>
                      Holiday Year
                    </label>
                    <input
                      type="number"
                      value={importYear}
                      onChange={e => setImportYear(parseInt(e.target.value, 10) || dayjs().year())}
                      min="2020"
                      max="2035"
                      style={{
                        width: 90, padding: "4px 8px", border: "1.5px solid #CBD5E1",
                        borderRadius: 6, fontSize: "0.82rem", fontWeight: 700, outline: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Download Sample Template */}
                <button
                  type="button"
                  onClick={() => downloadHolidayTemplate(importRegion, importYear)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "7px 14px", borderRadius: 8,
                    border: "1.5px solid #2563EB", background: "#EFF6FF", color: "#1D4ED8",
                    fontSize: "0.76rem", fontWeight: 700, cursor: "pointer",
                  }}
                  title="Download an Excel template pre-formatted with Month, Date, Day, Holiday Name, and Holiday Type"
                >
                  <Icon.Download size={14} color="#1D4ED8" />
                  Download Sample Template (.xlsx)
                </button>
              </div>

              {/* Upload Dropzone */}
              <div
                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={e => {
                  e.preventDefault();
                  setIsDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) processUploadedFile(file);
                }}
                onClick={() => {
                  const input = document.getElementById("holiday-excel-input");
                  if (input) input.click();
                }}
                style={{
                  border: isDragOver ? "2px dashed #2563EB" : "2px dashed #CBD5E1",
                  borderRadius: 12,
                  padding: "24px 16px",
                  textAlign: "center",
                  cursor: "pointer",
                  background: isDragOver ? "#EFF6FF" : "#FAFAFA",
                  transition: "all 0.15s ease",
                }}
              >
                <input
                  id="holiday-excel-input"
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) processUploadedFile(file);
                  }}
                  style={{ display: "none" }}
                />
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon.Upload size={22} color="#475569" />
                  </div>
                  <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#1E293B" }}>
                    {importFileName ? (
                      <span style={{ color: "#059669" }}>Selected: {importFileName}</span>
                    ) : (
                      "Click to choose an Excel file or drag and drop here"
                    )}
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "#64748B" }}>
                    Supported formats: .xlsx, .xls, .csv • Required columns: Month, Date, Day, Holiday Name, Holiday Type
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {importError && (
                <div style={{
                  padding: "10px 14px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600,
                  background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA",
                }}>
                  {importError}
                </div>
              )}

              {/* Parsed Holidays Preview */}
              {parsedHolidays.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <h5 style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>
                      Preview Parsed Holidays ({parsedHolidays.length} rows)
                    </h5>
                    <div style={{ display: "flex", gap: 8 }}>
                      <span style={{
                        fontSize: "0.72rem", fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                        background: "#ECFDF5", color: "#059669", border: "1px solid #A7F3D0",
                      }}>
                        ✓ {validCount} Ready to Import
                      </span>
                      {invalidCount > 0 && (
                        <span style={{
                          fontSize: "0.72rem", fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                          background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA",
                        }}>
                          ⚠ {invalidCount} Invalid (will be skipped)
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{
                    maxHeight: 220, overflowY: "auto", border: "1px solid #E2E8F0",
                    borderRadius: 8, background: "#fff",
                  }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.76rem" }}>
                      <thead>
                        <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", color: "#475569", textAlign: "left" }}>
                          <th style={{ padding: "8px 10px", width: 36 }}>#</th>
                          <th style={{ padding: "8px 10px" }}>Month</th>
                          <th style={{ padding: "8px 10px" }}>Date</th>
                          <th style={{ padding: "8px 10px" }}>Day</th>
                          <th style={{ padding: "8px 10px" }}>Holiday Name</th>
                          <th style={{ padding: "8px 10px" }}>Holiday Type</th>
                          <th style={{ padding: "8px 10px" }}>Resolved Date</th>
                          <th style={{ padding: "8px 10px", width: 70 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedHolidays.map((row, i) => (
                          <tr
                            key={i}
                            style={{
                              borderBottom: "1px solid #F1F5F9",
                              background: row.isValid ? (i % 2 === 0 ? "#FFFFFF" : "#F8FAFC") : "#FFF1F2",
                            }}
                          >
                            <td style={{ padding: "7px 10px", color: "#94A3B8" }}>{row.rowNum}</td>
                            <td style={{ padding: "7px 10px", fontWeight: 600 }}>{row.rawMonth}</td>
                            <td style={{ padding: "7px 10px" }}>{row.rawDate}</td>
                            <td style={{ padding: "7px 10px", color: "#475569" }}>{row.rawDay}</td>
                            <td style={{ padding: "7px 10px", fontWeight: 700, color: "#1E293B" }}>{row.holidayName}</td>
                            <td style={{ padding: "7px 10px" }}>
                              <span style={{
                                padding: "2px 7px", borderRadius: 999, fontSize: "0.68rem", fontWeight: 700,
                                background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D",
                              }}>
                                {row.holidayType}
                              </span>
                            </td>
                            <td style={{ padding: "7px 10px", fontFamily: "monospace", color: row.isValid ? "#059669" : "#DC2626", fontWeight: 600 }}>
                              {row.resolvedDate || "—"}
                            </td>
                            <td style={{ padding: "7px 10px" }}>
                              {row.isValid ? (
                                <span style={{ color: "#059669", fontWeight: 700, fontSize: "0.72rem" }}>✓ Valid</span>
                              ) : (
                                <span style={{ color: "#DC2626", fontWeight: 700, fontSize: "0.70rem" }} title={row.errorMsg}>✕ Invalid</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 24px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC",
            }}>
              <div style={{ fontSize: "0.72rem", color: "#64748B" }}>
                Holidays will be assigned to <b>{HOLIDAY_REGIONS.find(r => r.key === importRegion)?.label}</b> for year <b>{importYear}</b>.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { setShowImportModal(false); setParsedHolidays([]); setImportFileName(""); setImportError(null); }}
                  style={{
                    padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1",
                    background: "#fff", color: "#475569", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExecuteBatchImport}
                  disabled={isImporting || validCount === 0}
                  style={{
                    padding: "8px 20px", borderRadius: 8, border: "none",
                    background: validCount === 0 ? "#94A3B8" : "#059669",
                    color: "#fff", fontSize: "0.78rem", fontWeight: 700,
                    cursor: validCount === 0 || isImporting ? "not-allowed" : "pointer",
                    boxShadow: validCount > 0 ? "0 2px 4px rgba(5, 150, 105, 0.2)" : "none",
                  }}
                >
                  {isImporting ? "Importing..." : `Import ${validCount} Holidays`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LeaveApprovalsView = ({ refreshTrigger, employees = [], setEmployees, isAccordion }) => {
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(true);
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;

  const [editingBalanceEmp, setEditingBalanceEmp] = useState(null);
  const [editBalanceValue, setEditBalanceValue] = useState("");

  const handleSaveBalance = async (empUsername) => {
    try {
      const res = await fetch(`/timesheets/leaves/user/${empUsername}/balance`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`
        },
        body: JSON.stringify({ balance: Number(editBalanceValue) })
      });
      if (res.ok) {
        setEmployees(prev => prev.map(e => e.username === empUsername ? { ...e, leaveBalance: Number(editBalanceValue) } : e));
        setEditingBalanceEmp(null);
      } else {
        alert("Failed to update leave balance");
      }
    } catch (e) {
      console.error(e);
      alert("Error occurred while saving leave balance");
    }
  };

  const fetchApproved = useCallback(async () => {
    try {
      const res = await fetch("/timesheets/leaves/approved", { headers: { Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}` } });
      if (res.ok) setApprovedLeaves(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingLeaves(false); }
  }, []);

  useEffect(() => {
    fetchApproved();
  }, [fetchApproved, refreshTrigger]);

  // Group leaves by employee, listing all active team members
  const empMap = {};
  const activeEmps = employees.filter(emp => emp.active !== false);
  activeEmps.forEach(emp => {
    empMap[emp.username] = {
      employeeName: emp.fullName,
      username: emp.username,
      clientName: emp.clientName || "Internal",
      leaves: []
    };
  });
  approvedLeaves.forEach(leave => {
    const key = leave.username;
    if (empMap[key]) {
      empMap[key].leaves.push(leave);
    }
  });
  const empList = Object.values(empMap);
  const totalPages = Math.ceil(empList.length / PAGE_SIZE);
  const pagedEmps = empList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const content = loadingLeaves ? (
    <div className="em-empty"><p>Loading...</p></div>
  ) : empList.length === 0 ? (
    <div className="em-empty"><Icon.CheckCircle size={28} color="#A7F3D0" /><p>No approved leaves found.</p></div>
  ) : (<>
    <table className="em-table">
      <thead><tr><th>Employee</th><th>Client</th><th>Total Leaves</th><th>Leave Balance</th></tr></thead>
      <tbody>
        {pagedEmps.map(emp => {
          const currentYear = dayjs().year();
          const currentYearLeaves = emp.leaves.filter(l => dayjs(l.startDate).year() === currentYear);
          const totalDays = currentYearLeaves.reduce((s, l) => s + Number(l.days || 0), 0);
          const isOpen = expandedEmp === emp.username;
          const empInfo = employees.find(e => e.username === emp.username);
          const yearlyAllotment = empInfo ? (empInfo.leaveBalance !== undefined ? empInfo.leaveBalance : 10) : 10;
          const balance = yearlyAllotment - totalDays;
          const isEditing = editingBalanceEmp === emp.username;
          return (
            <React.Fragment key={emp.username}>
              <tr style={{ cursor: "pointer", background: isOpen ? "#EFF6FF" : undefined }} onClick={() => setExpandedEmp(isOpen ? null : emp.username)}>
                <td><div className="em-emp-cell"><div className="em-emp-avatar">{(emp.employeeName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{emp.employeeName}</div><div style={{ fontSize: "0.7rem", color: "#64748B" }}>{emp.username}</div></div></div></td>
                <td style={{ fontSize: "0.8rem", color: "#475569" }}>{emp.clientName}</td>
                <td>
                  {isEditing ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                      <input
                        type="number"
                        value={editBalanceValue}
                        onChange={e => setEditBalanceValue(e.target.value)}
                        style={{ width: 50, padding: "3px 6px", border: "1.5px solid #2563EB", borderRadius: 6, fontSize: "0.8rem", outline: "none" }}
                        autoFocus
                      />
                      <button onClick={() => handleSaveBalance(emp.username)} style={{ padding: "3px 8px", background: "#10B981", color: "#fff", border: "none", borderRadius: 6, fontSize: "0.68rem", fontWeight: 700, cursor: "pointer" }}>Save</button>
                      <button onClick={() => setEditingBalanceEmp(null)} style={{ padding: "3px 8px", background: "#EF4444", color: "#fff", border: "none", borderRadius: 6, fontSize: "0.68rem", fontWeight: 700, cursor: "pointer" }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="em-hours-badge">{yearlyAllotment}</span>
                      <button className="em-action-btn view" title="Edit Yearly Leaves" onClick={(e) => { e.stopPropagation(); setEditingBalanceEmp(emp.username); setEditBalanceValue(yearlyAllotment.toString()); }} style={{ padding: 4, minWidth: 0, width: 20, height: 20 }}>
                        <Icon.Edit size={10} color="currentColor" />
                      </button>
                    </div>
                  )}
                </td>
                <td>
                  <span className="em-days-badge" style={balance <= 2 ? { background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA" } : undefined}>
                    {balance} Day{balance !== 1 ? "s" : ""}
                  </span>
                </td>
              </tr>
              {isOpen && (
                <tr><td colSpan={4} style={{ padding: "12px 20px", background: "#F8FBFF", borderTop: "1px solid #E2EEFF" }}>
                  {emp.leaves.length === 0 ? (
                    <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748B" }}>No approved leave requests for this employee.</p>
                  ) : (
                    <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse" }}>
                      <thead><tr style={{ color: "#64748B", fontWeight: 700, borderBottom: "1px solid #E2EEFF" }}><th style={{ padding: "8px", textAlign: "left" }}>Leave Type</th><th style={{ padding: "8px", textAlign: "left" }}>From</th><th style={{ padding: "8px", textAlign: "left" }}>To</th><th style={{ padding: "8px", textAlign: "left" }}>Taken</th><th style={{ padding: "8px", textAlign: "left" }}>Reason</th><th style={{ padding: "8px", textAlign: "left" }}>Applied On</th></tr></thead>
                      <tbody>
                        {emp.leaves.map(leave => (
                          <tr key={leave.id} style={{ borderTop: "1px solid #F1F5F9" }}>
                            <td style={{ padding: "8px" }}><span className="em-type-pill">{leave.leaveType}</span></td>
                            <td style={{ padding: "8px" }}>{leave.startDate}</td>
                            <td style={{ padding: "8px" }}>{leave.endDate}</td>
                            <td style={{ padding: "8px" }}><span className="em-days-badge">{leave.days} Day{leave.days > 1 ? "s" : ""}</span></td>
                            <td style={{ padding: "8px", color: "#475569" }}>{leave.reason || "—"}</td>
                            <td style={{ padding: "8px", color: "#64748B" }}>{leave.appliedAt ? dayjs(leave.appliedAt).format("MMM D, YYYY") : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </td></tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
    {totalPages > 1 && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #E2EEFF", fontSize: "0.8rem", color: "#64748B" }}>
        <span>Page {currentPage} of {totalPages}</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button disabled={currentPage <= 1} onClick={() => { setCurrentPage(p => p - 1); setExpandedEmp(null); }}
            style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #E2EEFF", background: currentPage <= 1 ? "#F8FAFC" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: currentPage <= 1 ? "#CBD5E1" : "#2563EB", cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}>
            ‹ Previous
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const page = i + 1;
            return <button key={page} onClick={() => { setCurrentPage(page); setExpandedEmp(null); }}
              style={{ padding: "6px 10px", borderRadius: 8, border: page === currentPage ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF", background: page === currentPage ? "#EFF6FF" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: page === currentPage ? "#2563EB" : "#64748B", cursor: "pointer", minWidth: 32 }}>
              {page}
            </button>;
          })}
          <button disabled={currentPage >= totalPages} onClick={() => { setCurrentPage(p => p + 1); setExpandedEmp(null); }}
            style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #E2EEFF", background: currentPage >= totalPages ? "#F8FAFC" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: currentPage >= totalPages ? "#CBD5E1" : "#2563EB", cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}>
            Next ›
          </button>
        </div>
      </div>
    )}
  </>);

  if (isAccordion) return content;

  return (
    <div className="em-card">
      <div className="em-card-header"><h3>Leave Approvals</h3></div>
      {content}
    </div>
  );
};

/* ── AI Analysis Computation Engine ── */
const generateExecutiveSummary = (items, type) => {
  if (items.length === 0) {
    if (type.includes("highlights")) return "General operational satisfaction reported.";
    if (type.includes("concerns")) return "No critical pain points recorded.";
    return ["Maintain standard operating procedures."];
  }

  const cleanAndExtractKeywords = (text) => {
    const stopWords = new Set([
      "the", "a", "an", "and", "but", "or", "for", "with", "about", "against", "between",
      "into", "through", "during", "before", "after", "above", "below", "to", "from",
      "up", "down", "in", "out", "on", "off", "over", "under", "again", "further",
      "then", "once", "here", "there", "when", "where", "why", "how", "all", "any",
      "both", "each", "few", "more", "most", "other", "some", "such", "no", "nor",
      "not", "only", "own", "same", "so", "than", "too", "very", "can", "will",
      "just", "should", "now", "was", "were", "has", "have", "had", "does", "did",
      "been", "being", "is", "are", "am", "great", "good", "nice", "excellent", "went", "well",
      "bad", "wrong", "needs", "improve", "should", "could", "would", "had", "some", "many",
      "our", "their", "them", "they", "this", "that", "these", "those",
      "work", "time", "feedback", "employee", "employees", "employer", "management", "company",
      "people", "things", "aspect", "issues", "problems", "task", "tasks", "week", "month", "year",
      "retro", "retrospective", "submit", "submission", "day", "days"
    ]);
    const words = text.toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter(w => w.length > 3 && !stopWords.has(w));
    return [...new Set(words)];
  };

  const positiveThemes = {
    "team": "enhanced team collaboration",
    "colleague": "strong peer-to-peer relationships",
    "collaboration": "collaborative workflow execution",
    "coordination": "effective project coordination",
    "together": "unified team effort",
    "peer": "peer support channels",
    "support": "workspace support structures",
    "help": "collaborative troubleshooting assistance",
    "manager": "supportive leadership direction",
    "management": "responsive management feedback",
    "leader": "proactive leadership alignment",
    "employer": "supportive workplace practices",
    "guidance": "clear guidance from project leads",
    "milestone": "milestone completion achievements",
    "success": "project delivery successes",
    "goal": "consistent goal attainment",
    "deliver": "reliable deliverable standards",
    "completed": "successful task completion metrics",
    "progress": "steady project progress",
    "remote": "workplace flexibility",
    "hybrid": "hybrid schedule configurations",
    "hours": "flexible scheduling guidelines",
    "schedule": "balanced schedule tracking",
    "flexible": "work-life balance accommodations",
    "flexibility": "scheduling flexibility",
    "tool": "tooling integration efficiency",
    "software": "software stack optimization",
    "system": "system architecture stability",
    "setup": "streamlined onboarding setup",
    "git": "git workflow efficiency",
    "ide": "development environment productivity",
    "database": "database query performance",
    "server": "server load stability",
    "code": "codebase health and clean code standards",
    "architecture": "robust system architecture",
    "deploy": "automated deployment cycles",
    "testing": "comprehensive test coverage",
    "meeting": "productive alignment meetings",
    "sync": "frequent team sync-ups"
  };

  const negativeThemes = {
    "communication": "internal communication gaps",
    "gap": "visibility alignment gaps",
    "friction": "operational coordination friction",
    "silo": "departmental silos",
    "workload": "workload distribution imbalances",
    "stress": "workplace stress indicators",
    "burnout": "team burnout risks",
    "overtime": "excessive overtime requirements",
    "busy": "high task load congestion",
    "blocker": "dependency blockers",
    "license": "software licensing delays",
    "credentials": "credential access delays",
    "access": "access permission delays",
    "crash": "system crash vulnerabilities",
    "slow": "response latency issues",
    "down": "system downtime events",
    "api": "api integration challenges",
    "error": "system error rates",
    "delay": "timeline schedule slippages",
    "late": "milestone delivery delays",
    "deadline": "aggressive deadline pressures"
  };

  const synthesizeThematicOverview = (items, field, prefix, defaultVal, themeMap) => {
    const allText = items
      .map(item => (item[field] || "").toLowerCase())
      .join(" ");

    const detectedPhrases = [];
    for (const [keyword, phrase] of Object.entries(themeMap)) {
      if (allText.includes(keyword)) {
        detectedPhrases.push(phrase);
      }
    }

    // Dynamic Fallback for unmatched words: convert raw keywords into professional parameters
    if (detectedPhrases.length === 0) {
      const topics = cleanAndExtractKeywords(allText).slice(0, 3);
      if (topics.length > 0) {
        topics.forEach(word => {
          let theme = `${word} parameters`;
          if (word.endsWith("ing")) theme = `${word} processes`;
          else if (word.endsWith("ion") || word.endsWith("ions")) theme = `${word} workflows`;
          else if (word.endsWith("ment") || word.endsWith("ments")) theme = `${word} alignment`;
          else if (word.endsWith("ity") || word.endsWith("ities")) theme = `${word} stability`;
          detectedPhrases.push(theme);
        });
      }
    }

    if (detectedPhrases.length === 0) {
      return defaultVal;
    }

    const uniquePhrases = [...new Set(detectedPhrases)].slice(0, 3);

    let combined = "";
    if (uniquePhrases.length === 1) {
      combined = uniquePhrases[0];
    } else if (uniquePhrases.length === 2) {
      combined = uniquePhrases[0] + " and " + uniquePhrases[1];
    } else {
      combined = uniquePhrases.slice(0, -1).join(", ") + ", and " + uniquePhrases[uniquePhrases.length - 1];
    }

    return `${prefix} ${combined}.`;
  };

  const generateThematicRecommendations = (items, field, defaultRecs) => {
    const allText = items
      .map(item => (item[field] || "").toLowerCase())
      .join(" ");

    const recommendationMap = {
      "Establish regular team syncs and dedicated communication channels to bridge coordination gaps.": ["meeting", "sync", "communication", "channel", "slack", "call", "talk"],
      "Optimize workload sharing, adjust task assignments, and implement transparent progress tracking.": ["share", "work", "balance", "assign", "task", "jira", "burnout", "stress"],
      "Conduct a comprehensive resource audit and tooling configuration check to resolve technical blockages.": ["resource", "tool", "equip", "software", "license", "hardware", "setup", "access"],
      "Introduce targeted skill training, mentoring programs, or knowledge sharing workshops.": ["train", "skill", "mentoring", "learning", "course", "education", "workshop"],
      "Refine internal documentation, wiki pages, and onboarding guides to streamline new procedures.": ["documentation", "onboarding", "guide", "readme", "doc", "wiki"],
      "Implement flexible schedules or hybrid work policy templates to support team balance.": ["hybrid", "remote", "hours", "flexible", "home", "flexibility"],
      "Review backend services, server memory allocation, or database optimization strategies.": ["database", "server", "slow", "performance", "backend", "api", "crash", "query"],
      "Establish shared client-facing communication channels or slack protocols.": ["collaboration", "align", "workshop", "communication", "channel", "slack"],
      "Provide weekly structured progress dashboards to increase project visibility.": ["clarity", "update", "report", "visibility", "dashboard"],
      "Optimize review cycles and feedback loops to accelerate delivery turnaround.": ["speed", "velocity", "turnaround", "review", "cycle"]
    };

    const matches = [];
    for (const [rec, keywords] of Object.entries(recommendationMap)) {
      if (keywords.some(k => allText.includes(k))) {
        matches.push(rec);
      }
    }

    // Dynamic Fallback for unmatched suggestion words
    if (matches.length === 0) {
      const topics = cleanAndExtractKeywords(allText).slice(0, 2);
      if (topics.length > 0) {
        topics.forEach(word => {
          let rec = `Review alignment and operational strategies regarding ${word} workflows.`;
          if (word.endsWith("ing")) rec = `Optimize team coordination related to ${word} processes.`;
          else if (word.endsWith("ment")) rec = `Refine parameters for ${word} alignment.`;
          matches.push(rec);
        });
      }
    }

    // De-duplicate similar recommendations using word overlap
    const getCleanWords = (str) => {
      const stopWords = new Set([
        "the", "a", "an", "and", "but", "or", "for", "with", "about", "to", "from", "in", "on", "at", "by", "of", "regarding", "related", "strategies", "alignment", "parameters", "processes", "workflows", "optimize", "review", "refine", "team", "coordination", "operational", "establish", "conduct", "audit", "check", "resolve", "support", "balance"
      ]);
      return new Set(
        str.toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .split(/\s+/)
          .filter(w => w.length > 3 && !stopWords.has(w))
      );
    };

    const uniqueMatches = [];
    for (const rec of matches) {
      const wordsRec = getCleanWords(rec);
      const isDuplicate = uniqueMatches.some(existingRec => {
        const wordsExisting = getCleanWords(existingRec);
        if (wordsRec.size === 0 || wordsExisting.size === 0) return false;

        let intersection = 0;
        wordsRec.forEach(w => {
          if (wordsExisting.has(w)) intersection++;
        });

        const overlapPercentage = intersection / Math.min(wordsRec.size, wordsExisting.size);
        return overlapPercentage >= 0.5; // 50% overlap of clean topic words
      });

      if (!isDuplicate) {
        uniqueMatches.push(rec);
      }
    }

    return uniqueMatches.length > 0 ? uniqueMatches.slice(0, 5) : defaultRecs;
  };

  if (type === "emp_highlights") {
    return synthesizeThematicOverview(items, "wentWell", "Employee feedback highlights positive achievements regarding:", "Employee feedback highlights positive achievements across standard operational parameters.", positiveThemes);
  }

  if (type === "emp_concerns") {
    return synthesizeThematicOverview(items, "wentWrong", "Key operational concerns identified centered on:", "No critical operational concerns or pain points were detected.", negativeThemes);
  }

  if (type === "emp_actions") {
    return generateThematicRecommendations(items, "improvements", [
      "Optimize team alignment and refine work schedules to match delivery timelines.",
      "Maintain standard documentation reviews and regular progress tracking templates."
    ]);
  }

  if (type === "client_highlights") {
    return synthesizeThematicOverview(items, "strengths", "Client feedback indicated strong satisfaction, particularly highlighting:", "Client feedback indicated general satisfaction with deliverables and standard timelines.", positiveThemes);
  }

  if (type === "client_concerns") {
    return synthesizeThematicOverview(items, "gaps", "Main client concerns and project alignment issues focused on:", "No major project concerns or alignment gaps were raised by client managers.", negativeThemes);
  }

  if (type === "client_actions") {
    return generateThematicRecommendations(items, "suggestions", [
      "Review client reporting frequency and feedback integration timelines.",
      "Schedule bi-weekly status review syncs with client representatives."
    ]);
  }

  return "N/A";
};

const computeAiAnalysis = (data) => {
  console.log("computeAiAnalysis inputs:", data);
  const totalEmpSubmissions = data.employees.length;
  const totalClientSubmissions = data.clients.length;

  const isWordGibberish = (word) => {
    const w = word.toLowerCase().replace(/[^a-z]/g, "");
    if (w.length < 3) return false;
    const vowels = (w.match(/[aeiouy]/g) || []).length;
    if (vowels === 0) return true;
    if (vowels / w.length < 0.18) return true;
    if (/asdf|sdfg|dfgh|fghj|ghjk|hjkl|qwer|wert|erty|rtyp|zxcv|xcvb/.test(w)) return true;
    return false;
  };

  const employeeTexts = data.employees.map(e => `${e.wentWell} ${e.wentWrong} ${e.improvements}`);
  const clientTexts = data.clients.map(c => `${c.strengths} ${c.gaps} ${c.suggestions}`);
  const allTexts = [...employeeTexts, ...clientTexts].join(" ");

  const words = allTexts.split(/\s+/).filter(w => w.trim().length > 2);
  const gibberishWords = words.filter(w => isWordGibberish(w));
  const isGibberishDetected = words.length > 0 && (gibberishWords.length / words.length) > 0.4;

  const posKeywords = ["good", "great", "excellent", "well", "happy", "love", "nice", "success", "milestone", "best", "perfect", "supportive", "milestones", "spirit"];
  const negKeywords = ["bad", "wrong", "slow", "fail", "error", "worst", "hard", "difficult", "gap", "lack", "short", "frustrat", "block", "latency", "friction"];

  const analyzedIds = [
    ...data.employees.map(e => e.id),
    ...data.clients.map(c => c.id)
  ];

  if (isGibberishDetected) {
    return {
      isGibberish: true,
      analyzedIds,
      sentiment: "Low Confidence (Gibberish Detected)",
      concernText: "Unintelligible data inputs detected from submitted feedback files. Offending patterns include: " + [...new Set(gibberishWords)].slice(0, 4).join(", ") + ".",
      employeeAnalysis: {
        sentiment: "Low Confidence",
        highlight: "N/A",
        concern: "N/A",
        actions: ["Request team members to input coherent feedback descriptions rather than placeholder characters."]
      },
      clientAnalysis: {
        sentiment: "Low Confidence",
        highlight: "N/A",
        concern: "N/A",
        actions: ["Implement client-side text validation to block keyboard-mashing characters."]
      },
      timestamp: dayjs().format("MMM D, YYYY h:mm A")
    };
  }

  let employeeAnalysis = {
    sentiment: "No employee feedback submitted yet",
    highlight: "N/A",
    concern: "N/A",
    actions: []
  };

  let clientAnalysis = {
    sentiment: "No client feedback submitted yet",
    highlight: "N/A",
    concern: "N/A",
    actions: []
  };

  if (totalEmpSubmissions > 0) {
    let empScore = 0;
    const empWords = employeeTexts.join(" ").split(/\s+/).filter(w => w.trim().length > 2);
    empWords.forEach(w => {
      const cleanW = w.toLowerCase();
      if (posKeywords.some(pk => cleanW.includes(pk))) empScore += 1;
      if (negKeywords.some(nk => cleanW.includes(nk))) empScore -= 1;
    });

    let empSentiment = "Balanced / Neutral";
    if (empScore > 1) empSentiment = "Highly Positive";
    else if (empScore < -1) empSentiment = "Needs Attention";

    employeeAnalysis = {
      sentiment: `${empSentiment} (${Math.min(75 + empScore * 5, 98)}% Sentiment)`,
      highlight: generateExecutiveSummary(data.employees, "emp_highlights"),
      concern: generateExecutiveSummary(data.employees, "emp_concerns"),
      actions: generateExecutiveSummary(data.employees, "emp_actions")
    };
  }

  if (totalClientSubmissions > 0) {
    let clientScore = 0;
    const clientWords = clientTexts.join(" ").split(/\s+/).filter(w => w.trim().length > 2);
    clientWords.forEach(w => {
      const cleanW = w.toLowerCase();
      if (posKeywords.some(pk => cleanW.includes(pk))) clientScore += 1;
      if (negKeywords.some(nk => cleanW.includes(nk))) clientScore -= 1;
    });

    let clientSentiment = "Balanced / Neutral";
    if (clientScore > 1) clientSentiment = "Highly Positive";
    else if (clientScore < -1) clientSentiment = "Needs Attention";

    clientAnalysis = {
      sentiment: `${clientSentiment} (${Math.min(75 + clientScore * 5, 98)}% Sentiment)`,
      highlight: generateExecutiveSummary(data.clients, "client_highlights"),
      concern: generateExecutiveSummary(data.clients, "client_concerns"),
      actions: generateExecutiveSummary(data.clients, "client_actions")
    };
  }

  const resultObj = {
    isGibberish: false,
    analyzedIds,
    employeeAnalysis,
    clientAnalysis,
    timestamp: dayjs().format("MMM D, YYYY h:mm A")
  };
  console.log("computeAiAnalysis result:", resultObj);
  return resultObj;
};

/* ── EmployerFeedbackView ── */
const EmployerFeedbackView = ({ isDashboard = false }) => {
  const [subTab, setSubTab] = useState("employees"); // 'employees' | 'clients'
  const [feedbackData, setFeedbackData] = useState({ employees: [], clients: [] });

  // AI analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisStep, setAnalysisStep] = useState(0);

  // Read all feedback from localStorage
  const loadAllFeedback = useCallback(() => {
    const employeesRaw = [];
    const clientsRaw = [];
    const currentMonthStr = dayjs().format("MMMM YYYY");

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith("workforcemgt_feedback_")) {
        const username = key.replace("workforcemgt_feedback_", "");
        try {
          const list = JSON.parse(localStorage.getItem(key)) || [];
          list.forEach(item => {
            if (item.month === currentMonthStr) {
              employeesRaw.push({ ...item, username });
            }
          });
        } catch (e) { console.error(e); }
      } else if (key.startsWith("workforcemgt_client_feedback_")) {
        const username = key.replace("workforcemgt_client_feedback_", "");
        try {
          const list = JSON.parse(localStorage.getItem(key)) || [];
          list.forEach(item => {
            if (item.month === currentMonthStr) {
              clientsRaw.push({ ...item, username });
            }
          });
        } catch (e) { console.error(e); }
      }
    }

    // Sort by date submitted
    const sortByDate = (a, b) => {
      return new Date(b.dateSubmitted || 0) - new Date(a.dateSubmitted || 0);
    };

    const sortedEmployees = employeesRaw.sort(sortByDate);
    const sortedChronologically = [...sortedEmployees].reverse();
    let anonCounter = 1;
    const anonMap = {};
    sortedChronologically.forEach(item => {
      if (item.isAnonymous) {
        anonMap[item.username] = `Anonymous ${anonCounter++}`;
      }
    });

    const mappedEmployees = sortedEmployees.map(item => {
      if (item.isAnonymous) {
        return {
          ...item,
          username: anonMap[item.username] || "Anonymous"
        };
      }
      return item;
    });

    const result = {
      employees: mappedEmployees,
      clients: clientsRaw.sort(sortByDate)
    };

    setFeedbackData(result);
    return result;
  }, []);

  // Load initial state, listen for focus and storage events to reload feedback dynamically
  useEffect(() => {
    const reloadFeedback = () => {
      const data = loadAllFeedback();
      if (data.employees.length > 0 || data.clients.length > 0) {
        const result = computeAiAnalysis(data);
        setAnalysisResult(result);
      } else {
        setAnalysisResult(null);
      }
    };

    // Load immediately on mount
    reloadFeedback();

    // Reload when switching tab back (window gains focus)
    window.addEventListener("focus", reloadFeedback);

    // Reload when storage changes in another tab
    const handleStorageChange = (e) => {
      if (e.key && (e.key.startsWith("workforcemgt_feedback_") || e.key.startsWith("workforcemgt_client_feedback_"))) {
        reloadFeedback();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("focus", reloadFeedback);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [loadAllFeedback]);

  // Run AI analysis simulation
  const handleAiAnalysis = () => {
    // Reload latest feedback immediately from localStorage to avoid stale state
    const data = loadAllFeedback();
    setAnalyzing(true);
    setAnalysisStep(1);

    // Simulate steps of AI analysis
    setTimeout(() => {
      setAnalysisStep(2);
      setTimeout(() => {
        setAnalysisStep(3);
        setTimeout(() => {
          const result = computeAiAnalysis(data);
          setAnalysisResult(result);
          localStorage.setItem("workforcemgt_ai_analysis_result", JSON.stringify(result));
          setAnalyzing(false);
          setAnalysisStep(0);
        }, 1000);
      }, 1000);
    }, 800);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
      {/* Clean White Automated Insights Card */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, padding: "16px 24px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", textAlign: "left" }}>

        {/* Header with optional Run button */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F1F5F9", paddingBottom: 10, marginBottom: 14, gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#0F172A", display: "inline-flex", alignItems: "center", gap: 6 }}>
            🤖 Automated Executive Insights
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {analysisResult && !analyzing && <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>Generated {analysisResult.timestamp}</span>}
            <button
              onClick={handleAiAnalysis}
              disabled={analyzing || (feedbackData.employees.length === 0 && feedbackData.clients.length === 0)}
              style={{
                background: analyzing || (feedbackData.employees.length === 0 && feedbackData.clients.length === 0) ? "#94A3B8" : "#2563EB",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 12px",
                fontWeight: 700,
                fontSize: "0.75rem",
                cursor: analyzing || (feedbackData.employees.length === 0 && feedbackData.clients.length === 0) ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => {
                if (!analyzing && (feedbackData.employees.length > 0 || feedbackData.clients.length > 0)) {
                  e.target.style.background = "#1D4ED8";
                }
              }}
              onMouseLeave={(e) => {
                if (!analyzing && (feedbackData.employees.length > 0 || feedbackData.clients.length > 0)) {
                  e.target.style.background = "#2563EB";
                }
              }}
            >
              {analyzing ? "Analyzing..." : "Run AI Analysis"}
              {!analyzing && <Icon.ArrowRight size={12} color="#fff" />}
            </button>
          </div>
        </div>

        {/* AI Processing Screen */}
        {analyzing && (
          <div style={{ background: "#F8FAFC", borderRadius: 10, padding: 12, border: "1px solid #E2E8F0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <div style={{ width: 14, height: 14, borderRadius: 999, border: "2px solid #2563EB", borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
              <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569" }}>
                {analysisStep === 1 && "Phase 1: Gathering feedback records from employees and clients..."}
                {analysisStep === 2 && "Phase 2: Running Natural Language Processing (NLP) to extract themes..."}
                {analysisStep === 3 && "Phase 3: Synthesizing actionable recommendations for improvement..."}
              </span>
            </div>
            <div style={{ height: 4, background: "#E2E8F0", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", background: "#2563EB", width: `${(analysisStep / 3) * 100}%`, transition: "width 0.5s ease" }} />
            </div>
          </div>
        )}

        {/* AI Results Report */}
        {analysisResult && !analyzing ? (
          analysisResult.isGibberish ? (
            <div style={{ textAlign: "left" }}>
              <span style={{ fontSize: "0.65rem", fontWeight: 700, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 2 }}>📈 Sentiment Index</span>
              <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#1E293B", display: "block", marginBottom: 8 }}>{analysisResult.sentiment}</span>
              <span style={{ fontSize: "0.65rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 2 }}>⚠️ Warning Details</span>
              <p style={{ fontSize: "0.8rem", fontWeight: 500, color: "#334155", margin: "0 0 8px" }}>{analysisResult.concernText}</p>
              <span style={{ fontSize: "0.65rem", fontWeight: 700, color: "#8B5CF6", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>💡 Recommended Action Items</span>
              <ul style={{ margin: 0, paddingLeft: 14, fontSize: "0.78rem", color: "#334155" }}>
                {analysisResult.employeeAnalysis.actions.concat(analysisResult.clientAnalysis.actions).map((item, idx) => (
                  <li key={idx} style={{ lineHeight: 1.3 }}>{item}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, textAlign: "left" }}>
              {/* Employee Insights Panel */}
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 18px" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: "0.85rem", color: "#0F172A", borderBottom: "1px solid #E2E8F0", paddingBottom: 6, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>👥 Employee Insights</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: "0.8rem", color: "#334155" }}>
                  <div>
                    <span style={{ fontWeight: 700, color: "#2563EB", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 2 }}>📈 Sentiment Summary</span>
                    <span style={{ fontWeight: 500 }}>{analysisResult.employeeAnalysis.sentiment}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 700, color: "#10B981", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 2 }}>🟢 Highlights & Successes</span>
                    <span style={{ lineHeight: 1.4 }}>{analysisResult.employeeAnalysis.highlight}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 700, color: "#EF4444", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 2 }}>🔴 Key Pain Point / Concern</span>
                    <span style={{ lineHeight: 1.4 }}>{analysisResult.employeeAnalysis.concern}</span>
                  </div>
                  {analysisResult.employeeAnalysis.actions.length > 0 && (
                    <div>
                      <span style={{ fontWeight: 700, color: "#8B5CF6", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 4 }}>💡 Recommendations</span>
                      <ul style={{ margin: 0, paddingLeft: 14, display: "flex", flexDirection: "column", gap: 2 }}>
                        {analysisResult.employeeAnalysis.actions.map((act, idx) => <li key={idx} style={{ lineHeight: 1.3 }}>"{act}"</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Client Insights Panel */}
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 18px" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: "0.85rem", color: "#0F172A", borderBottom: "1px solid #E2E8F0", paddingBottom: 6, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>💼 Client Insights</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: "0.8rem", color: "#334155" }}>
                  <div>
                    <span style={{ fontWeight: 700, color: "#2563EB", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 2 }}>📈 Sentiment Summary</span>
                    <span style={{ fontWeight: 500 }}>{analysisResult.clientAnalysis.sentiment}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 700, color: "#10B981", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 2 }}>🟢 Top Strength Area</span>
                    <span style={{ lineHeight: 1.4 }}>{analysisResult.clientAnalysis.highlight}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 700, color: "#EF4444", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 2 }}>🔴 Collaboration Gaps</span>
                    <span style={{ lineHeight: 1.4 }}>{analysisResult.clientAnalysis.concern}</span>
                  </div>
                  {analysisResult.clientAnalysis.actions.length > 0 && (
                    <div>
                      <span style={{ fontWeight: 700, color: "#8B5CF6", fontSize: "0.65rem", textTransform: "uppercase", display: "block", marginBottom: 4 }}>💡 Suggestions</span>
                      <ul style={{ margin: 0, paddingLeft: 14, display: "flex", flexDirection: "column", gap: 2 }}>
                        {analysisResult.clientAnalysis.actions.map((act, idx) => <li key={idx} style={{ lineHeight: 1.3 }}>"{act}"</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ) : (
          !analyzing && (
            <div style={{ fontSize: "0.8rem", color: "#64748B", textAlign: "center", padding: "12px 0" }}>
              No feedback analysis generated yet. Please enter feedback from employee/client accounts.
            </div>
          )
        )}
      </div>

      {/* Tabs and feedback logs */}
      {!isDashboard && (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, padding: "32px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", display: "flex", flexDirection: "column", gap: 24, textAlign: "left" }}>

          {/* Sub-tabs header */}
          <div style={{ display: "flex", alignItems: "center", borderBottom: "1.5px solid #F1F5F9", paddingBottom: 0, gap: 24 }}>
            {[
              { key: "employees", label: `Employee Submissions (${feedbackData.employees.length})` },
              { key: "clients", label: `Client Submissions (${feedbackData.clients.length})` }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setSubTab(tab.key)}
                style={{
                  background: "none",
                  border: "none",
                  padding: "10px 4px 14px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  color: subTab === tab.key ? "#2563EB" : "#94A3B8",
                  borderBottom: subTab === tab.key ? "2.5px solid #2563EB" : "2.5px solid transparent",
                  cursor: "pointer",
                  transition: "all 0.15s ease"
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Feedback List */}
          {subTab === "employees" ? (
            feedbackData.employees.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "45px 10px", color: "#94A3B8", fontSize: "0.875rem", textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
                <Icon.FileText size={32} color="#CBD5E1" />
                <p style={{ margin: 0, color: "#64748B" }}>No employee feedback has been submitted this month.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {feedbackData.employees.map((item) => (
                  <div key={item.id} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px", background: "#fff", display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F1F5F9", paddingBottom: 12, margin: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A" }}>
                          {item.username.startsWith("Anonymous") ? item.username : `@${item.username}`}
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", fontSize: "0.7rem", fontWeight: 600, background: "#EFF6FF", color: "#2563EB", padding: "3px 8px", borderRadius: 999, border: "1px solid #DBEAFE" }}>
                          Employee
                        </span>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>{item.month} · {item.dateSubmitted}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Highlights */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#10B981", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Highlights & Successes</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.wentWell}</p>
                        </div>
                      </div>

                      {/* Challenges */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Challenges & Gaps</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.wentWrong}</p>
                        </div>
                      </div>

                      {/* Actionable Ideas */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#F59E0B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                            <line x1="9" y1="18" x2="15" y2="18" />
                            <line x1="10" y1="22" x2="14" y2="22" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Actionable Ideas & Suggestions</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.improvements}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            feedbackData.clients.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "45px 10px", color: "#94A3B8", fontSize: "0.875rem", textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
                <Icon.FileText size={32} color="#CBD5E1" />
                <p style={{ margin: 0, color: "#64748B" }}>No client feedback has been submitted this month.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {feedbackData.clients.map((item) => (
                  <div key={item.id} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px", background: "#fff", display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F1F5F9", paddingBottom: 12, margin: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A" }}>@{item.username}</span>
                        <span style={{ display: "inline-flex", alignItems: "center", fontSize: "0.7rem", fontWeight: 600, background: "#F0FDF4", color: "#16A34A", padding: "3px 8px", borderRadius: 999, border: "1px solid #DCFCE7" }}>
                          Client Manager
                        </span>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>{item.month} · {item.dateSubmitted}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Strengths */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#10B981", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Strengths & Highlights</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.strengths}</p>
                        </div>
                      </div>

                      {/* Gaps */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Collaboration Gaps</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.gaps}</p>
                        </div>
                      </div>

                      {/* Suggestions */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#F59E0B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                            <line x1="9" y1="18" x2="15" y2="18" />
                            <line x1="10" y1="22" x2="14" y2="22" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Key Suggestions & Improvements</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.suggestions}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

const EmployerHome = () => {
  const { username, name } = useAuth();

  const getGreetingIcon = (greeting) => {
    const g = (greeting || "").toLowerCase();
    if (g.includes("morning")) {
      return <Icon.Sun size={28} color="#2563EB" strokeWidth={1.5} />;
    } else if (g.includes("afternoon")) {
      return <Icon.Sun size={28} color="#2563EB" strokeWidth={1.5} />;
    } else {
      return <Icon.Moon size={28} color="#2563EB" strokeWidth={1.5} />;
    }
  };



  const [employees, setEmployees] = useState([]);
  const [empStatusFilter, setEmpStatusFilter] = useState("active"); // "active" | "inactive" | "all"
  const [clients, setClients] = useState([]);
  const [clientStatusFilter, setClientStatusFilter] = useState("active"); // "active" | "inactive" | "all"
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedFilterClient, setSelectedFilterClient] = useState("ALL");
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [clientEmployees, setClientEmployees] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [approvedRefreshTrigger, setApprovedRefreshTrigger] = useState(0);
  const [showPendingLeaves, setShowPendingLeaves] = useState(true);
  const [showLeaveApprovals, setShowLeaveApprovals] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [activeNav, setActiveNav] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab") || params.get("nav");
      if (tab && NAV.some(n => n.key === tab.toLowerCase())) {
        return tab.toLowerCase();
      }
    } catch (e) {
      // ignore
    }
    return "dashboard";
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab") || params.get("nav");
      if (tab && NAV.some(n => n.key === tab.toLowerCase())) {
        setActiveNav(tab.toLowerCase());
      }
    } catch (e) {
      // ignore
    }
  }, []);
  const [showSupport, setShowSupport] = useState(false);
  const [expandedTsId, setExpandedTsId] = useState(null);
  const [expandedRangeEmp, setExpandedRangeEmp] = useState(null);
  const [approvalsPage, setApprovalsPage] = useState(1);
  const [rejectModal, setRejectModal] = useState(null); // { leaveId }
  const [rejectReason, setRejectReason] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [detailEmp, setDetailEmp] = useState(null); // employee detail panel
  const [detailProfile, setDetailProfile] = useState(null); // fetched profile data
  const [detailEditing, setDetailEditing] = useState(false);
  const [detailForm, setDetailForm] = useState({});
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailMsg, setDetailMsg] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [approvalsTab, setApprovalsTab] = useState("weekly"); // "weekly" or "monthly"
  const [showWeeklyApprovals, setShowWeeklyApprovals] = useState(true);
  const [showEmployeeTimesheets, setShowEmployeeTimesheets] = useState(false);
  const [calcFrom, setCalcFrom] = useState("");
  const [calcTo, setCalcTo] = useState("");

  const activeEmployees = useMemo(() => employees.filter(e => e.active !== false), [employees]);
  const activeClients = useMemo(() => clients.filter(c => c.active !== false), [clients]);
  const activePendingLeaves = useMemo(() => {
    return pendingLeaves.filter(leave => {
      const emp = employees.find(e => e.username === leave.username);
      return !emp || emp.active !== false;
    });
  }, [pendingLeaves, employees]);

  const { weekStart, weekEnd } = useMemo(() => getSunSat(), []);

  useEffect(() => {
    if (!isFilterDropdownOpen) return;
    const close = () => setIsFilterDropdownOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [isFilterDropdownOpen]);

  // Add User / Client modal state
  const [createType, setCreateType] = useState(null); // "employee" | "client" | null
  const [createForm, setCreateForm] = useState({
    fullName: "",
    username: "",
    startDate: dayjs().format("YYYY-MM-DD"),
    clientName: "Internal",
    timezone: "Asia/Kolkata",
    workPermit: "Citizen",
    workPermitExpiry: ""
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null); // { username, temporaryPassword, name, role, email }
  const [copiedField, setCopiedField] = useState(null); // "username" | "password" | "all" | null

  // Fetch employees
  const fetchEmployeesList = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`/timesheets/getUsers/${username}`, { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        setEmployees((data?.usersResponseList || []).map(u => ({
          fullName: u.fullName, username: u.userName, active: u.active, startDate: u.startDate, clientName: u.clientName, leaveBalance: u.leaveBalance, clientNotesEnabled: u.clientNotesEnabled,
          temporaryPassword: u.temporaryPassword,
        })));
      }
    } catch (e) { console.error(e); }
  }, [username]);

  // Fetch clients (client_managers)
  const fetchClientsList = useCallback(async () => {
    try {
      const res = await fetch("/timesheets/getClients", { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        setClients((data?.usersResponseList || []).map(u => ({
          fullName: u.fullName, username: u.userName, active: u.active,
          temporaryPassword: u.temporaryPassword,
        })));
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchEmployeesList();
  }, [fetchEmployeesList]);

  useEffect(() => {
    fetchClientsList();
  }, [fetchClientsList]);

  const openCreateModal = (type) => {
    setCreateType(type);
    setCreateError(null);
    setCreatedCredentials(null);
    setCopiedField(null);
    setCreateForm({
      fullName: "",
      username: "",
      startDate: dayjs().format("YYYY-MM-DD"),
      clientName: "Internal",
      timezone: "Asia/Kolkata",
      workPermit: "Citizen",
      workPermitExpiry: ""
    });
  };

  const copyToClipboard = (text, fieldKey) => {
    if (!navigator.clipboard) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    } else {
      navigator.clipboard.writeText(text);
    }
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const handleCreateUserSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!createForm.fullName.trim()) {
      setCreateError("Full name is required.");
      return;
    }
    setCreateLoading(true);
    setCreateError(null);
    try {
      const isEmp = createType === "employee";
      const isAmerica = isAmericaTz(createForm.timezone);
      const payload = {
        name: createForm.fullName.trim(),
        fullName: createForm.fullName.trim(),
        username: createForm.username.trim() || undefined,
        role: isEmp ? "employee" : "client_manager",
        startDate: isEmp ? createForm.startDate : undefined,
        clientName: isEmp ? (createForm.clientName || "Internal") : undefined,
        timezone: createForm.timezone || "Asia/Kolkata",
        workPermit: (isEmp && isAmerica) ? (createForm.workPermit || "Citizen") : undefined,
        workPermitExpiry: (isEmp && isAmerica && createForm.workPermit !== "Citizen") ? (createForm.workPermitExpiry || undefined) : undefined,
      };

      const res = await fetch("/timesheets/employer/createUser", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...hdr() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to create account.");
      }

      // Success! Show credentials modal
      setCreatedCredentials({
        username: data.username,
        temporaryPassword: data.temporaryPassword,
        name: data.name,
        role: data.role,
        email: data.email
      });
      setCreateType(null);

      // Refresh lists
      fetchEmployeesList();
      fetchClientsList();
    } catch (err) {
      setCreateError(err.message || "Something went wrong.");
    } finally {
      setCreateLoading(false);
    }
  };

  // Fetch employees for a selected client
  useEffect(() => {
    if (!selectedClient) { setClientEmployees([]); return; }
    // For "Internal", filter locally from all active employees
    if (selectedClient.username === "__internal__") {
      const internalEmps = activeEmployees.filter(e => !e.clientName || e.clientName === "Internal");
      setClientEmployees(internalEmps.map(e => ({ fullName: e.fullName, username: e.username, active: e.active })));
      return;
    }
    (async () => {
      try {
        const res = await fetch(`/timesheets/getUsers/${selectedClient.username}`, { headers: hdr() });
        if (res.ok) {
          const data = await res.json();
          const activeList = (data?.usersResponseList || []).filter(u => u.active !== false);
          setClientEmployees(activeList.map(u => ({
            fullName: u.fullName, username: u.userName, active: u.active,
          })));
        }
      } catch (e) { console.error(e); }
    })();
  }, [selectedClient, activeEmployees]);

  // Fetch all timesheets (1-year range) for active employees
  useEffect(() => {
    if (!activeEmployees.length) { setAllRows([]); return; }
    setLoading(true);
    const today = new Date();
    const twoYearsAgo = new Date(); twoYearsAgo.setFullYear(today.getFullYear() - 2);
    // extend end to cover the full current week (next Saturday)
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay()) + 7);
    const ws = fmt(twoYearsAgo), we = fmt(endOfWeek);
    (async () => {
      try {
        const results = await Promise.all(activeEmployees.map(async emp => {
          const res = await fetch(`/timesheets/operations/byDates/${emp.username}?weekStart=${ws}&weekEnd=${we}`, { headers: hdr() });
          if (!res.ok) return [];
          const data = await res.json();
          return (data?.timesheetsResponseList || []).map(ts => ({
            employee: emp,
            timesheet: {
              id: ts.timesheetId, week_start: ts.weekStart, week_end: ts.weekEnd,
              total_hours: ts.totalHours, status: ts.status,
              daily_hours: { sun: ts.hoursPerDay.sunday, mon: ts.hoursPerDay.monday, tue: ts.hoursPerDay.tuesday, wed: ts.hoursPerDay.wednesday, thu: ts.hoursPerDay.thursday, fri: ts.hoursPerDay.friday, sat: ts.hoursPerDay.saturday },
              submittedAt: ts.submittedAt,
              comment: ts.clientComment || ts.comment,
              clientComment: ts.clientComment || ts.comment,
              employerComment: ts.employerComment,
              approvedBy: ts.approvedBy,
              approvedAt: ts.approvedAt,
            },
          }));
        }));
        const seen = new Set();
        const deduped = results.flat().filter(r => { if (seen.has(r.timesheet.id)) return false; seen.add(r.timesheet.id); return true; });
        setAllRows(deduped.sort((a, b) => new Date(b.timesheet.week_start) - new Date(a.timesheet.week_start)));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [activeEmployees]);

  // Fetch this week's timesheets for active employees
  useEffect(() => {
    if (!activeEmployees.length) { setRows([]); return; }
    (async () => {
      try {
        const results = await Promise.all(activeEmployees.map(async emp => {
          const res = await fetch(`/timesheets/operations/byDates/${emp.username}?weekStart=${weekStart}&weekEnd=${weekEnd}`, { headers: hdr() });
          if (!res.ok) return { employee: emp, timesheet: null };
          const data = await res.json();
          const ts = (data?.timesheetsResponseList || [])[0];
          if (!ts) return { employee: emp, timesheet: null };
          return {
            employee: emp,
            timesheet: {
              id: ts.timesheetId, week_start: ts.weekStart, week_end: ts.weekEnd,
              total_hours: ts.totalHours, status: ts.status,
              daily_hours: { sun: ts.hoursPerDay.sunday, mon: ts.hoursPerDay.monday, tue: ts.hoursPerDay.tuesday, wed: ts.hoursPerDay.wednesday, thu: ts.hoursPerDay.thursday, fri: ts.hoursPerDay.friday, sat: ts.hoursPerDay.saturday },
              submittedAt: ts.submittedAt,
              comment: ts.clientComment || ts.comment,
              clientComment: ts.clientComment || ts.comment,
              employerComment: ts.employerComment,
            },
          };
        }));
        setRows(results);
      } catch (e) { console.error(e); }
    })();
  }, [activeEmployees, weekStart, weekEnd]);

  // Fetch pending leaves
  const fetchLeaves = useCallback(async () => {
    try {
      const res = await fetch("/timesheets/leaves/pending", { headers: hdr() });
      if (res.ok) setPendingLeaves(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  // Fetch notifications
  const fetchNotifs = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`/timesheets/notifications/unread/${username}`, { headers: hdr() });
      if (res.ok) setNotifications(await res.json());
    } catch (e) { console.error(e); }
  }, [username]);

  useEffect(() => { fetchNotifs(); const iv = setInterval(fetchNotifs, 30000); return () => clearInterval(iv); }, [fetchNotifs]);

  // Actions
  const handleLeaveAction = async (leaveId, status, rejectionReason = "") => {
    try {
      const res = await fetch(`/timesheets/leaves/${leaveId}/status`, {
        method: "PATCH", headers: { "Content-Type": "application/json", ...hdr() },
        body: JSON.stringify({ status, rejectionReason }),
      });
      if (res.ok) {
        setPendingLeaves(prev => prev.filter(l => l.id !== leaveId));
        setApprovedRefreshTrigger(prev => prev + 1);
      }
    } catch (e) { console.error(e); }
  };

  const handleRejectClick = (leaveId) => {
    setRejectReason("");
    setRejectModal({ leaveId });
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) return;
    await handleLeaveAction(rejectModal.leaveId, "REJECTED", rejectReason.trim());
    setRejectModal(null);
    setRejectReason("");
  };

  const markAllRead = async () => {
    try {
      await fetch(`/timesheets/notifications/read-all/${username}`, { method: "PATCH", headers: hdr() });
      setNotifications([]);
    } catch (e) { console.error(e); }
  };

  const toggleActive = async (uname) => {
    try {
      const res = await fetch(`/timesheets/users/${uname}/toggleActive`, { method: "PATCH", headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        setEmployees(prev => prev.map(e => e.username === uname ? { ...e, active: data.active } : e));
        setClients(prev => prev.map(c => c.username === uname ? { ...c, active: data.active } : c));
      }
    } catch (e) { console.error(e); }
  };

  const openEmployeeDetail = async (emp) => {
    setDetailEmp(emp);
    setDetailEditing(false);
    setDetailMsg(null);
    try {
      const res = await fetch(`/timesheets/users/${emp.username}/profile`, { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        // Merge with employee list data as fallback for empty fields
        const merged = {
          ...data,
          name: data.name || emp.fullName || "",
          startDate: data.startDate || emp.startDate || "",
          clientName: data.clientName || emp.clientName || "Internal",
          role: data.role || "Employee",
          active: data.active !== undefined ? data.active : emp.active,
          clientNotesEnabled: data.clientNotesEnabled !== undefined ? data.clientNotesEnabled : (emp.clientNotesEnabled || false),
          workPermit: data.workPermit || emp.workPermit || "Citizen",
          workPermitExpiry: data.workPermitExpiry || emp.workPermitExpiry || "",
        };
        setDetailProfile(merged);
        setDetailForm(merged);
      } else {
        // If profile API fails, use employee list data
        setDetailProfile({
          username: emp.username,
          name: emp.fullName || "",
          email: "",
          mobileNumber: "",
          startDate: emp.startDate || "",
          clientName: emp.clientName || "Internal",
          timezone: "America/New_York",
          role: "Employee",
          active: emp.active,
          clientNotesEnabled: emp.clientNotesEnabled || false,
          workPermit: emp.workPermit || "Citizen",
          workPermitExpiry: emp.workPermitExpiry || "",
        });
        setDetailForm({
          username: emp.username,
          name: emp.fullName || "",
          email: "",
          mobileNumber: "",
          startDate: emp.startDate || "",
          clientName: emp.clientName || "Internal",
          timezone: "America/New_York",
          role: "Employee",
          active: emp.active,
          clientNotesEnabled: emp.clientNotesEnabled || false,
          workPermit: emp.workPermit || "Citizen",
          workPermitExpiry: emp.workPermitExpiry || "",
        });
      }
    } catch (e) { console.error(e); }
  };

  const saveEmployeeDetail = async () => {
    if (!detailEmp) return;
    setDetailSaving(true);
    setDetailMsg(null);
    try {
      const res = await fetch(`/timesheets/users/${detailEmp.username}/updateProfile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...hdr() },
        body: JSON.stringify({
          name: detailForm.name,
          email: detailForm.email,
          mobileNumber: detailForm.mobileNumber,
          clientName: detailForm.clientName,
          timezone: detailForm.timezone,
          startDate: detailForm.startDate,
          dateOfBirth: detailForm.dateOfBirth,
          clientNotesEnabled: detailForm.clientNotesEnabled,
          workPermit: detailForm.workPermit || "Citizen",
          workPermitExpiry: detailForm.workPermit === "Citizen" ? null : (detailForm.workPermitExpiry || null),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setDetailProfile(data);
        setDetailForm(data);
        setDetailEditing(false);
        setDetailMsg({ type: "success", text: "Profile updated successfully!" });
        // Update local employees list
        setEmployees(prev => prev.map(e => e.username === detailEmp.username ? {
          ...e,
          fullName: data.name,
          startDate: data.startDate,
          clientName: data.clientName,
          clientNotesEnabled: data.clientNotesEnabled,
          workPermit: data.workPermit,
          workPermitExpiry: data.workPermitExpiry
        } : e));
      } else {
        setDetailMsg({ type: "error", text: "Failed to update profile." });
      }
    } catch (e) {
      setDetailMsg({ type: "error", text: "Error updating profile." });
    } finally { setDetailSaving(false); }
  };

  const updateTimesheetStatus = async (tsId, newStatus) => {
    try {
      const res = await fetch(`/timesheets/operations/updateTimeSheetStatus/${tsId}`, {
        method: "PATCH", headers: { "Content-Type": "application/json", ...hdr() },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setAllRows(prev => prev.map(r => r.timesheet?.id === tsId ? { ...r, timesheet: { ...r.timesheet, status: newStatus } } : r));
        setRows(prev => prev.map(r => r.timesheet?.id === tsId ? { ...r, timesheet: { ...r.timesheet, status: newStatus } } : r));
      }
    } catch (e) { console.error(e); }
  };

  // Derived stats
  const totalEmp = activeEmployees.length;
  const submitted = rows.filter(r => r.timesheet && r.timesheet.status !== "Not Submitted" && r.employee?.active !== false).length;
  const pendingTimesheets = allRows.filter(r => (r.timesheet?.status === "Pending" || r.timesheet?.status === "Submitted") && r.employee?.active !== false);
  const approvedCount = allRows.filter(r => r.timesheet?.status === "Approved" && r.employee?.active !== false).length;
  const rejectedCount = allRows.filter(r => r.timesheet?.status === "Rejected" && r.employee?.active !== false).length;
  const notSubmitted = rows.filter(r => !r.timesheet && r.employee?.active !== false).length;
  const totalHrsThisWeek = rows.filter(r => r.employee?.active !== false).reduce((s, r) => s + Number(r.timesheet?.total_hours || 0), 0);
  const weekPct = totalEmp > 0 ? Math.round((submitted / totalEmp) * 100) : 0;

  // Recent activity from notifications
  const recentActivity = notifications.slice(0, 5).map(n => ({
    text: n.message || "Notification",
    time: n.createdAt ? dayjs(n.createdAt).format("MMM D, YYYY · h:mm A") : "",
    icon: n.type?.includes("APPROVED") ? "green" : n.type?.includes("LEAVE") ? "amber" : "blue",
  }));

  return (
    <div className="em-shell">
      {/* SIDEBAR */}
      <nav className="em-sidebar" aria-label="Employer navigation">
        <p className="em-nav-section-label">Main Menu</p>
        {NAV.map(item => (
          <button key={item.key} className={`em-nav-item ${activeNav === item.key ? "active" : ""}`}
            onClick={() => setActiveNav(item.key)}
            aria-current={activeNav === item.key ? "page" : undefined}>
            {item.icon === "Home" && <Icon.Home size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "Briefcase" && <Icon.Briefcase size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "Users" && <Icon.Users size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "Calendar" && <Icon.Calendar size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "CheckCircle" && <Icon.CheckCircle size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "Target" && <Icon.Target size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "BarChart" && <Icon.BarChart size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "Grid" && <Icon.Grid size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "Badge" && <Icon.Badge size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "Bell" && <Icon.Bell size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.icon === "MessageSquare" && <Icon.MessageSquare size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
            {item.label}
            {item.key === "leaves" && activePendingLeaves.length > 0 && (
              <span className="em-nav-badge">{activePendingLeaves.length}</span>
            )}
            {item.key === "approvals" && pendingTimesheets.length > 0 && (
              <span className="em-nav-badge">{pendingTimesheets.length}</span>
            )}
            {item.key === "notifications" && notifications.length > 0 && (
              <span className="em-nav-badge">{notifications.length}</span>
            )}
          </button>
        ))}

        {/* <p className="em-nav-section-label" style={{ marginTop: 16 }}>Overview</p>
        <div style={{ padding: "0 10px", display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            { label: "Total Clients", value: clients.length },
            { label: "Total Employees", value: totalEmp },
            { label: "Leave Requested", value: pendingLeaves.length },
            { label: "Pending Approvals", value: pendingTimesheets.length },
          ].map(s => (
            <div key={s.label} className="em-overview-item">
              <span>{s.label}</span>
              <span className="em-overview-value">{s.value}</span>
            </div>
          ))}
        </div> */}

        {/* <div className="em-sidebar-footer">
          <div className="em-help-card">
            <div style={{ width: 28, height: 28, borderRadius: 50, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 6px" }}>
              <Icon.AlertCircle size={14} color="#2563EB"/>
            </div>
            <h4>Need Help?</h4>
            <p>We're here to assist you</p>
            <button className="em-help-btn" onClick={() => setShowSupport(true)}>Contact Support</button>
          </div>
        </div> */}
      </nav>

      {/* MAIN */}
      <div className="em-main">

        {/* Bell icon for notifications */}
        <div style={{ position: "fixed", top: 13, right: 130, zIndex: 1400 }}>
          <button onClick={() => setShowNotifications(!showNotifications)}
            style={{ position: "relative", width: 34, height: 34, borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            aria-label="Notifications">
            <Icon.Bell size={18} color="#64748B" />
            {notifications.length > 0 && (
              <span style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: 999, background: "#EF4444", color: "#fff", fontSize: "0.6rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{notifications.length}</span>
            )}
          </button>
        </div>

        {/* Notifications dropdown */}
        {showNotifications && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 1500, background: "transparent" }} onClick={() => setShowNotifications(false)} />
            <div style={{ position: "fixed", top: 52, right: 16, width: 340, background: "#fff", border: "1.5px solid #E2EEFF", borderRadius: 14, boxShadow: "0 8px 32px rgba(37,99,235,0.14)", zIndex: 1600, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #F1F5F9", fontSize: "0.875rem", fontWeight: 700, color: "#1E293B" }}>
                <span>Notifications</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {notifications.length > 0 && <button style={{ fontSize: "0.75rem", color: "#2563EB", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }} onClick={() => { markAllRead(); setShowNotifications(false); }}>Mark all read</button>}
                  <button style={{ width: 24, height: 24, borderRadius: 6, background: "transparent", border: "1px solid #E2EEFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={() => setShowNotifications(false)} aria-label="Close"><Icon.X size={11} color="#64748B" /></button>
                </div>
              </div>
              {notifications.length === 0
                ? <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "36px 20px", color: "#64748B", fontSize: "0.84rem" }}><Icon.CheckCircle size={24} color="#86efac" /> You're all caught up!</div>
                : <ul style={{ listStyle: "none", maxHeight: 320, overflowY: "auto", margin: 0, padding: 0 }}>
                  {notifications.map(n => (
                    <li key={n.id} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "12px 16px", borderBottom: "1px solid #F8FBFF", borderLeft: `3px solid ${n.type?.includes("APPROVED") ? "#10B981" : n.type?.includes("LEAVE") ? "#F59E0B" : "#2563EB"}` }}>
                      <span style={{ fontSize: "0.8125rem", fontWeight: 500, color: "#1E293B" }}>{n.message}</span>
                      <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>{n.createdAt ? dayjs(n.createdAt).format("MMM D, YYYY · h:mm A") : ""}</span>
                    </li>
                  ))}
                </ul>}
            </div>
          </>
        )}

        <div className="em-center">

          {/* DASHBOARD */}
          {activeNav === "dashboard" && (<>
            {/* Hero */}
            <motion.div className="em-hero" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <div className="em-hero-accent-line" />
              <div style={{ display: "flex", alignItems: "center", gap: 20, position: "relative", zIndex: 3 }}>
                <div className="em-hero-icon-wrapper">
                  {getGreetingIcon(getGreeting())}
                </div>
                <div className="em-hero-text">
                  <h1 className="em-hero-title">{getGreeting()}, {name?.split(" ")[0] || "Employer"}!</h1>
                  <p className="em-hero-sub">Here's what's happening in your organization today.</p>
                </div>
              </div>
              <div className="em-hero-illo" aria-hidden="true">
                <div className="em-hero-illo-inner"><Icon.User size={34} color="#2563EB" strokeWidth={1.5} /></div>
              </div>
            </motion.div>

            {/* Stats */}
            <div className="em-stats-row">
              {[
                { label: "Total Employees", val: totalEmp, cls: "blue", icon: "Users" },
                { label: "Pending Leave Requests", val: activePendingLeaves.length, cls: "amber", icon: "Calendar" },
                { label: "Pending Timesheets", val: pendingTimesheets.length, cls: "purple", icon: "Clock" },
                { label: "Total Hours Logged", sub: "This Week", val: `${totalHrsThisWeek}h`, cls: "green", icon: "BarChart" },
              ].map((s, i) => (
                <motion.div key={s.label} className="em-stat-card"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.3 }}>
                  <div className={`em-stat-icon ${s.cls}`}>
                    {s.icon === "Users" && <Icon.Users size={18} color="#2563EB" />}
                    {s.icon === "Calendar" && <Icon.Calendar size={18} color="#F59E0B" />}
                    {s.icon === "Clock" && <Icon.Clock size={18} color="#8B5CF6" />}
                    {s.icon === "BarChart" && <Icon.BarChart size={18} color="#10B981" />}
                  </div>
                  <div>
                    <div className="em-stat-val">{s.val}</div>
                    <div className="em-stat-label">{s.label}</div>
                    {s.sub && <div className="em-stat-sub">{s.sub}</div>}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pending Leave Requests */}
            <div className="em-card">
              <div className="em-card-header">
                <h3>Pending Leave Requests ({activePendingLeaves.length})</h3>
                <button className="em-view-all" onClick={() => setActiveNav("leaves")}>View All</button>
              </div>
              {activePendingLeaves.length === 0 ? (
                <div className="em-empty"><Icon.CheckCircle size={28} color="#A7F3D0" /><p>No pending leave requests.</p></div>
              ) : (
                <div className="em-card-scroll">
                  <table className="em-table">
                    <thead><tr><th>Employee</th><th>Leave Type</th><th>From</th><th>To</th><th>Duration</th><th>Reason</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                      {activePendingLeaves.map(leave => {
                        const days = Math.abs(new Date(leave.endDate) - new Date(leave.startDate)) / 86400000 + 1;
                        return (
                          <tr key={leave.id}>
                            <td><div className="em-emp-cell"><div className="em-emp-avatar">{(leave.username || "?")[0].toUpperCase()}</div><span className="em-emp-name">{leave.username}</span></div></td>
                            <td><span className="em-type-pill">{leave.leaveType}</span></td>
                            <td className="em-date">{leave.startDate}</td>
                            <td className="em-date">{leave.endDate}</td>
                            <td><span className="em-days-badge">{days} Day{days > 1 ? "s" : ""}</span></td>
                            <td className="em-reason">{leave.reason || "—"}</td>
                            <td><span className="em-status-pill pending">Pending</span></td>
                            <td>
                              <div className="em-actions">
                                <button className="em-action-btn approve" onClick={() => handleLeaveAction(leave.id, "APPROVED")} title="Approve">✓</button>
                                <button className="em-action-btn reject" onClick={() => handleRejectClick(leave.id)} title="Reject">✕</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {activePendingLeaves.length > 5 && (
                <div className="em-card-footer"><button className="em-view-all-btn" onClick={() => setActiveNav("leaves")}>View All Leave Requests</button></div>
              )}
            </div>

            {/* Pending Timesheet Approvals */}
            <div className="em-card">
              <div className="em-card-header">
                <h3>Pending Timesheet Management ({pendingTimesheets.length})</h3>
                <button className="em-view-all" onClick={() => setActiveNav("approvals")}>View All</button>
              </div>
              {pendingTimesheets.length === 0 ? (
                <div className="em-empty"><Icon.CheckCircle size={28} color="#A7F3D0" /><p>No pending timesheet approvals.</p></div>
              ) : (
                <div className="em-card-scroll">
                  <table className="em-table">
                    <thead><tr><th>Employee</th><th>Week Range</th><th>Total Hours</th><th>Submitted On</th><th>Action</th></tr></thead>
                    <tbody>
                      {pendingTimesheets.slice(0, 4).map(({ employee, timesheet }) => (
                        <React.Fragment key={timesheet.id}>
                          <tr>
                            <td><div className="em-emp-cell"><div className="em-emp-avatar">{(employee.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{employee.fullName}</div></div></div></td>
                            <td className="em-date">{dayjs(timesheet.week_start).format("MMM D")} – {dayjs(timesheet.week_end).format("MMM D, YYYY")}</td>
                            <td><span className="em-hours-badge">{timesheet.total_hours}h 00m</span></td>
                            <td className="em-date">{timesheet.submittedAt ? dayjs(timesheet.submittedAt).format("MMM D, YYYY") : "—"}</td>
                            <td>
                              <div className="em-actions">
                                <button className="em-action-btn approve" onClick={() => updateTimesheetStatus(timesheet.id, "Approved")} title="Approve">✓</button>
                                <button className="em-action-btn reject" onClick={() => updateTimesheetStatus(timesheet.id, "Rejected")} title="Reject">✕</button>
                                <button className="em-action-btn view" title="View" onClick={() => setExpandedTsId(expandedTsId === timesheet.id ? null : timesheet.id)}><Icon.Eye size={12} color="currentColor" /></button>
                              </div>
                            </td>
                          </tr>
                          {expandedTsId === timesheet.id && (
                            <tr><td colSpan={5} style={{ padding: "12px 16px", background: "#F8FBFF" }}>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8, textAlign: "center", fontSize: "0.78rem" }}>
                                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                                  <div key={d} style={{ fontWeight: 700, color: "#64748B" }}>
                                    {d}<br /><span style={{ fontWeight: 500, color: "#94A3B8", fontSize: "0.72rem" }}>{dayjs(timesheet.week_start).day(0).add(i, "day").format("M/D")}</span>
                                  </div>
                                ))}
                                {["sun", "mon", "tue", "wed", "thu", "fri", "sat"].map(d => <div key={d} style={{ fontWeight: 600, color: "#0F172A" }}>{timesheet.daily_hours?.[d] ?? 0}h</div>)}
                              </div>
                              {(timesheet.clientComment || timesheet.comment || timesheet.employerComment) && (
                                <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                                  {(timesheet.clientComment || timesheet.comment) && (
                                    <div style={{ flex: 1, minWidth: 180, padding: "8px 12px", background: "#fff", border: "1px solid #DBEAFE", borderRadius: 8, fontSize: "0.78rem", color: "#1E293B" }}>
                                      <div style={{ fontWeight: 700, color: "#2563EB", fontSize: "0.72rem", marginBottom: 2 }}>Note for Client:</div>
                                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>{timesheet.clientComment || timesheet.comment}</div>
                                    </div>
                                  )}
                                  {timesheet.employerComment && (
                                    <div style={{ flex: 1, minWidth: 180, padding: "8px 12px", background: "#fff", border: "1px solid #EDE9FE", borderRadius: 8, fontSize: "0.78rem", color: "#1E293B" }}>
                                      <div style={{ fontWeight: 700, color: "#7C3AED", fontSize: "0.72rem", marginBottom: 2 }}>Note for Employer:</div>
                                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>{timesheet.employerComment}</div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td></tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {pendingTimesheets.length > 4 && (
                <div className="em-card-footer"><button className="em-view-all-btn" onClick={() => setActiveNav("approvals")}>View All Timesheet Management</button></div>
              )}
            </div>

            {/* Feedback & AI Insights Hub */}
            <div style={{ marginTop: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
                <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Organization Feedback & AI Insights</h3>
              </div>
              <EmployerFeedbackView isDashboard={true} />
            </div>
          </>)}

          {/* CLIENTS VIEW */}
          {activeNav === "clients" && (() => {
            const internalClient = { fullName: "Internal", username: "__internal__", active: true };
            const allClients = [internalClient, ...clients];
            const activeCount = allClients.filter(c => c.active !== false).length;
            const inactiveCount = allClients.filter(c => !c.active).length;
            const displayClients = clientStatusFilter === "inactive"
              ? allClients.filter(c => !c.active)
              : allClients.filter(c => c.active !== false);

            return (
              <div className="em-card">
                <div className="em-card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <h3>Clients ({displayClients.length})</h3>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      className="em-view-all-btn"
                      style={{
                        background: "#2563EB",
                        color: "#fff",
                        border: "none",
                        padding: "7px 16px",
                        borderRadius: 8,
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        boxShadow: "0 2px 8px rgba(37,99,235,0.22)"
                      }}
                      onClick={() => openCreateModal("client")}
                    >
                      <Icon.Plus size={14} color="#fff" /> Add Client
                    </button>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <button
                        onClick={() => setClientStatusFilter("active")}
                        style={{
                          padding: "5px 12px",
                          borderRadius: 8,
                          border: clientStatusFilter === "active" ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF",
                          background: clientStatusFilter === "active" ? "#EFF6FF" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: clientStatusFilter === "active" ? "#2563EB" : "#64748B",
                          cursor: "pointer"
                        }}
                      >
                        Active ({activeCount})
                      </button>
                      <button
                        onClick={() => setClientStatusFilter("inactive")}
                        style={{
                          padding: "5px 12px",
                          borderRadius: 8,
                          border: clientStatusFilter === "inactive" ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF",
                          background: clientStatusFilter === "inactive" ? "#EFF6FF" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: clientStatusFilter === "inactive" ? "#2563EB" : "#64748B",
                          cursor: "pointer"
                        }}
                      >
                        Inactive ({inactiveCount})
                      </button>
                    </div>
                  </div>
                </div>
                {displayClients.length === 0 ? (
                  <div className="em-empty"><Icon.Briefcase size={28} color="#BFDBFE" /><p>No {clientStatusFilter} clients found.</p></div>
                ) : (
                  <table className="em-table">
                    <thead><tr><th>Client Name</th><th>Username</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                      {displayClients.map(client => (
                        <React.Fragment key={client.username}>
                          <tr style={{ background: selectedClient?.username === client.username ? "#EFF6FF" : undefined }}>
                            <td><div className="em-emp-cell"><div className="em-emp-avatar" style={{ background: "#F5F3FF", color: "#8B5CF6" }}>{(client.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{client.fullName}</div></div></div></td>
                            <td className="em-date">{client.username === "__internal__" ? "—" : client.username}</td>
                            <td><span className={`em-status-pill ${client.active ? "approved" : "rejected"}`}>{client.active ? "Active" : "Inactive"}</span></td>
                            <td>
                              <div className="em-actions">
                                <button className="em-view-all-btn" style={{ padding: "5px 14px" }} onClick={() => setSelectedClient(selectedClient?.username === client.username ? null : client)}>
                                  {selectedClient?.username === client.username ? "Hide Employees" : "View Employees"}
                                </button>
                                {client.username !== "__internal__" && (
                                  <button className={`em-action-btn ${client.active ? "reject" : "approve"}`} title={client.active ? "Deactivate" : "Activate"} onClick={() => toggleActive(client.username)}>{client.active ? "✕" : "✓"}</button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {selectedClient?.username === client.username && (
                            <tr><td colSpan={4} style={{ padding: "16px 20px", background: "#F8FBFF", borderTop: "1px solid #E2EEFF" }}>
                              <h4 style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A", marginBottom: 10 }}>
                                Employees under {client.fullName} ({clientEmployees.filter(e => e.active !== false).length})
                              </h4>
                              {clientEmployees.filter(e => e.active !== false).length === 0 ? (
                                <p style={{ fontSize: "0.8rem", color: "#94A3B8" }}>No active employees assigned to this client.</p>
                              ) : (
                                <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse" }}>
                                  <thead><tr style={{ borderBottom: "1px solid #E2EEFF" }}><th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: "#64748B", fontSize: "0.72rem", textTransform: "uppercase" }}>Employee</th><th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: "#64748B", fontSize: "0.72rem", textTransform: "uppercase" }}>Username</th><th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: "#64748B", fontSize: "0.72rem", textTransform: "uppercase" }}>Status</th></tr></thead>
                                  <tbody>
                                    {clientEmployees.filter(e => e.active !== false).map(emp => (
                                      <tr key={emp.username} style={{ borderBottom: "1px solid #F1F5F9" }}>
                                        <td style={{ padding: "8px 10px" }}><div className="em-emp-cell"><div className="em-emp-avatar">{(emp.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{emp.fullName}</div></div></div></td>
                                        <td style={{ padding: "8px 10px" }} className="em-date">{emp.username}</td>
                                        <td style={{ padding: "8px 10px" }}><span className={`em-status-pill ${emp.active ? "approved" : "rejected"}`}>{emp.active ? "Active" : "Inactive"}</span></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td></tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}

          {/* EMPLOYEES VIEW */}
          {activeNav === "employees" && (() => {
            const activeCount = employees.filter(e => e.active !== false).length;
            const inactiveCount = employees.filter(e => !e.active).length;
            const displayEmployees = empStatusFilter === "inactive"
              ? employees.filter(emp => !emp.active)
              : employees.filter(emp => emp.active !== false);

            return (
              <div className="em-card">
                <div className="em-card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <h3>Employees ({displayEmployees.length})</h3>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      style={{
                        background: "#2563EB",
                        color: "#fff",
                        border: "none",
                        padding: "7px 16px",
                        borderRadius: 8,
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        boxShadow: "0 2px 8px rgba(37,99,235,0.22)"
                      }}
                      onClick={() => openCreateModal("employee")}
                    >
                      <Icon.Plus size={14} color="#fff" /> Add Employee
                    </button>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <button
                        onClick={() => setEmpStatusFilter("active")}
                        style={{
                          padding: "5px 12px",
                          borderRadius: 8,
                          border: empStatusFilter === "active" ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF",
                          background: empStatusFilter === "active" ? "#EFF6FF" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: empStatusFilter === "active" ? "#2563EB" : "#64748B",
                          cursor: "pointer"
                        }}
                      >
                        Active ({activeCount})
                      </button>
                      <button
                        onClick={() => setEmpStatusFilter("inactive")}
                        style={{
                          padding: "5px 12px",
                          borderRadius: 8,
                          border: empStatusFilter === "inactive" ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF",
                          background: empStatusFilter === "inactive" ? "#EFF6FF" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: empStatusFilter === "inactive" ? "#2563EB" : "#64748B",
                          cursor: "pointer"
                        }}
                      >
                        Inactive ({inactiveCount})
                      </button>
                    </div>
                  </div>
                </div>
                {displayEmployees.length === 0 ? (
                  <div className="em-empty"><Icon.Users size={28} color="#94A3B8" /><p>No {empStatusFilter} employees found.</p></div>
                ) : (
                  <table className="em-table">
                    <thead><tr><th>Employee</th><th>Username</th><th>Start Date</th><th>Account</th><th>Latest Timesheet</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                      {displayEmployees.map(emp => {
                        const empRows = allRows.filter(r => r.employee.username === emp.username);
                        const latest = empRows[0]?.timesheet;
                        const status = latest?.status || "Not Submitted";
                        return (
                          <React.Fragment key={emp.username}>
                            <tr>
                              <td><div className="em-emp-cell"><div className="em-emp-avatar">{(emp.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{emp.fullName}</div></div></div></td>
                              <td className="em-date">{emp.username}</td>
                              <td className="em-date">{emp.startDate ? dayjs(emp.startDate).format("MMM D, YYYY") : "—"}</td>
                              <td><span className={`em-status-pill ${emp.active ? "approved" : "rejected"}`}>{emp.active ? "Active" : "Inactive"}</span></td>
                              <td className="em-date">{latest ? `${dayjs(latest.week_start).format("MMM D")} – ${dayjs(latest.week_end).format("MMM D")}` : "—"}</td>
                              <td><span className={`em-status-pill ${status.toLowerCase().replace(/\s+/g, "-")}`}>{status}</span></td>
                              <td>
                                <div className="em-actions">
                                  <button className="em-action-btn view" title="View Details" onClick={() => openEmployeeDetail(emp)}><Icon.Eye size={12} color="currentColor" /></button>
                                  <button className={`em-action-btn ${emp.active ? "reject" : "approve"}`} title={emp.active ? "Deactivate" : "Activate"} onClick={() => toggleActive(emp.username)}>{emp.active ? "✕" : "✓"}</button>
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}

          {/* INTERNAL VIEW */}
          {/* LEAVES VIEW */}
          {activeNav === "leaves" && (
            <div className="em-card">
              <div className="em-card-header">
                <h3>Leave Administration</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {/* Pending Leaves Accordion Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 20px",
                    background: "#F8FBFF",
                    borderBottom: "1px solid #E2EEFF",
                    cursor: "pointer",
                    userSelect: "none"
                  }}
                  onClick={() => setShowPendingLeaves(!showPendingLeaves)}
                >
                  <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A" }}>
                    {showPendingLeaves ? "▼" : "▶"} Pending Leave Requests ({activePendingLeaves.length})
                  </span>
                </div>

                {showPendingLeaves && (
                  <div style={{ borderBottom: "1px solid #E2EEFF" }}>
                    {activePendingLeaves.length === 0 ? (
                      <div className="em-empty" style={{ padding: "20px 0" }}><Icon.CheckCircle size={28} color="#A7F3D0" /><p>No pending leave requests found.</p></div>
                    ) : (
                      <div className="em-card-scroll">
                        <table className="em-table">
                          <thead><tr><th>Employee</th><th>Leave Type</th><th>From</th><th>To</th><th>Duration</th><th>Reason</th><th>Action</th></tr></thead>
                          <tbody>
                            {activePendingLeaves.map(leave => {
                              const days = Math.abs(new Date(leave.endDate) - new Date(leave.startDate)) / 86400000 + 1;
                              return (
                                <tr key={leave.id}>
                                  <td><div className="em-emp-cell"><div className="em-emp-avatar">{(leave.username || "?")[0].toUpperCase()}</div><span className="em-emp-name">{leave.username}</span></div></td>
                                  <td><span className="em-type-pill">{leave.leaveType}</span></td>
                                  <td className="em-date">{leave.startDate}</td>
                                  <td className="em-date">{leave.endDate}</td>
                                  <td><span className="em-days-badge">{days} Day{days > 1 ? "s" : ""}</span></td>
                                  <td className="em-reason">{leave.reason || "—"}</td>
                                  <td>
                                    <div className="em-actions">
                                      <button className="em-action-btn approve" onClick={() => handleLeaveAction(leave.id, "APPROVED")} title="Approve">✓</button>
                                      <button className="em-action-btn reject" onClick={() => handleRejectClick(leave.id)} title="Reject">✕</button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Leave Approvals Accordion Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 20px",
                    background: "#F8FBFF",
                    borderBottom: "1px solid #E2EEFF",
                    cursor: "pointer",
                    userSelect: "none",
                    borderTop: "1px solid #E2EEFF"
                  }}
                  onClick={() => setShowLeaveApprovals(!showLeaveApprovals)}
                >
                  <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A" }}>
                    {showLeaveApprovals ? "▼" : "▶"} Leave Balances & History
                  </span>
                </div>

                {showLeaveApprovals && (
                  <div style={{ padding: "0 0 16px 0" }}>
                    <LeaveApprovalsView refreshTrigger={approvedRefreshTrigger} employees={activeEmployees} setEmployees={setEmployees} isAccordion={true} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* APPROVALS VIEW */}
          {activeNav === "approvals" && (() => {
            // Group all timesheets by week_start
            const weekMap = {};
            allRows.forEach(({ employee, timesheet }) => {
              if (!timesheet) return;
              if (timesheet.status === "Approved" || timesheet.status === "Rejected") return;
              const ws = timesheet.week_start;
              if (!weekMap[ws]) weekMap[ws] = [];
              weekMap[ws].push({ employee, timesheet });
            });
            const sortedWeeks = Object.keys(weekMap).sort((a, b) => new Date(b) - new Date(a));
            const WEEKS_PER_PAGE = 5;
            const totalPages = Math.ceil(sortedWeeks.length / WEEKS_PER_PAGE);
            const currentApprovalsPage = Math.min(Math.max(1, approvalsPage), totalPages || 1);
            const pagedWeeks = sortedWeeks.slice((currentApprovalsPage - 1) * WEEKS_PER_PAGE, currentApprovalsPage * WEEKS_PER_PAGE);

            // Monthly view data
            const now = dayjs();
            const monthOptions = [];
            for (let i = 0; i < 24; i++) {
              monthOptions.push(now.subtract(i, "month").startOf("month"));
            }
            const selectedMonthValue = selectedMonth || monthOptions[0].format("YYYY-MM");
            const month = dayjs(selectedMonthValue + "-01");
            const monthStart = month.format("YYYY-MM-DD");
            const monthEnd = month.endOf("month").format("YYYY-MM-DD");
            const monthRows = allRows.filter(r => {
              const ws = r.timesheet?.week_start;
              const we = r.timesheet?.week_end || (ws ? dayjs(ws).add(6, "day").format("YYYY-MM-DD") : null);
              return ws && we && ws <= monthEnd && we >= monthStart;
            });
            const empMap = {};
            monthRows.forEach(({ employee, timesheet }) => {
              const key = employee.username;
              if (!empMap[key]) {
                empMap[key] = { employee, totalHours: 0, weeks: [] };
              }
              // Sum only hours worked on days within the selected month
              let hoursInMonth = 0;
              const start = dayjs(timesheet.week_start);
              const daysOfWeek = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
              daysOfWeek.forEach((day, index) => {
                const dayDate = start.add(index, "day");
                const dateStr = dayDate.format("YYYY-MM-DD");
                if (dateStr >= monthStart && dateStr <= monthEnd) {
                  hoursInMonth += Number(timesheet.daily_hours?.[day] || 0);
                }
              });
              empMap[key].totalHours += hoursInMonth;
              empMap[key].weeks.push({ ...timesheet, monthHours: hoursInMonth });
            });
            const empList = Object.values(empMap);

            return (
              <>
                <div className="em-card">
                  <div className="em-card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <h3>Timesheet Management</h3>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button onClick={() => setApprovalsTab("weekly")} style={{ padding: "6px 14px", borderRadius: 8, border: approvalsTab === "weekly" ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF", background: approvalsTab === "weekly" ? "#EFF6FF" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: approvalsTab === "weekly" ? "#2563EB" : "#64748B", cursor: "pointer" }}>Weekly Approvals</button>
                      <button onClick={() => setApprovalsTab("monthly")} style={{ padding: "6px 14px", borderRadius: 8, border: approvalsTab === "monthly" ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF", background: approvalsTab === "monthly" ? "#EFF6FF" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: approvalsTab === "monthly" ? "#2563EB" : "#64748B", cursor: "pointer" }}>Monthly View</button>
                    </div>
                  </div>

                  {approvalsTab === "weekly" && (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {/* Weekly Approvals Accordion Header */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 20px",
                          background: "#F8FBFF",
                          borderBottom: "1px solid #E2EEFF",
                          cursor: "pointer",
                          userSelect: "none"
                        }}
                        onClick={() => setShowWeeklyApprovals(!showWeeklyApprovals)}
                      >
                        <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A" }}>
                          {showWeeklyApprovals ? "▼" : "▶"} Weekly Approvals
                        </span>
                      </div>

                      {showWeeklyApprovals && (
                        sortedWeeks.length === 0 ? (
                          <div className="em-empty"><Icon.CheckCircle size={28} color="#A7F3D0" /><p>No timesheets found.</p></div>
                        ) : (<>
                          {pagedWeeks.map(ws => {
                            const weekRows = weekMap[ws];
                            const we = weekRows[0]?.timesheet.week_end;
                            return (
                              <div key={ws} style={{ marginBottom: 16 }}>
                                <h4 style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A", padding: "10px 20px", background: "#F8FBFF", borderBottom: "1px solid #E2EEFF" }}>
                                  {dayjs(ws).format("MMM D")} – {dayjs(we).format("MMM D, YYYY")}
                                </h4>
                                <div className="em-card-scroll">
                                  <table className="em-table">
                                    <thead><tr><th>Employee</th><th>Client</th><th>Total Hours</th><th>Status</th><th>Submitted On</th><th>Action</th></tr></thead>
                                    <tbody>
                                      {weekRows.map(({ employee, timesheet }) => (
                                        <React.Fragment key={timesheet.id}>
                                          <tr>
                                            <td><div className="em-emp-cell"><div className="em-emp-avatar">{(employee.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{employee.fullName}</div></div></div></td>
                                            <td style={{ fontSize: "0.8rem", color: "#475569" }}>{employee.clientName || "Internal"}</td>
                                            <td><span className="em-hours-badge">{timesheet.total_hours}h 00m</span></td>
                                            <td><span className={`em-status-pill ${timesheet.status?.toLowerCase().replace(/\s+/g, "-")}`}>{timesheet.status}</span></td>
                                            <td className="em-date">{timesheet.submittedAt ? dayjs(timesheet.submittedAt).format("MMM D, YYYY") : "—"}</td>
                                            <td>
                                              <div className="em-actions">
                                                {(timesheet.status === "Pending" || timesheet.status === "Submitted") && (
                                                  <>
                                                    <button className="em-action-btn approve" onClick={() => updateTimesheetStatus(timesheet.id, "Approved")} title="Approve">✓</button>
                                                    <button className="em-action-btn reject" onClick={() => updateTimesheetStatus(timesheet.id, "Rejected")} title="Reject">✕</button>
                                                  </>
                                                )}
                                                <button className="em-action-btn view" title="View Timesheet" onClick={() => setExpandedTsId(expandedTsId === timesheet.id ? null : timesheet.id)} style={{ marginLeft: 4 }}><Icon.Eye size={12} color="currentColor" /></button>
                                              </div>
                                            </td>
                                          </tr>
                                          {expandedTsId === timesheet.id && (
                                            <tr><td colSpan={6} style={{ padding: "12px 16px", background: "#F8FBFF" }}>
                                              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8, textAlign: "center", fontSize: "0.78rem" }}>
                                                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                                                  <div key={d}>
                                                    <div style={{ fontWeight: 700, color: "#64748B" }}>{d}</div>
                                                    <div style={{ fontWeight: 500, color: "#94A3B8", fontSize: "0.72rem" }}>{dayjs(timesheet.week_start).day(0).add(i, "day").format("M/D")}</div>
                                                    <div style={{ fontWeight: 600, color: "#0F172A", marginTop: 2 }}>{timesheet.daily_hours?.[d.toLowerCase().slice(0, 3)] ?? 0}h</div>
                                                  </div>
                                                ))}
                                              </div>
                                              {(timesheet.clientComment || timesheet.comment || timesheet.employerComment) && (
                                                <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                                                  {(timesheet.clientComment || timesheet.comment) && (
                                                    <div style={{ flex: 1, minWidth: 180, padding: "8px 12px", background: "#fff", border: "1px solid #DBEAFE", borderRadius: 8, fontSize: "0.78rem", color: "#1E293B" }}>
                                                      <div style={{ fontWeight: 700, color: "#2563EB", fontSize: "0.72rem", marginBottom: 2 }}>Note for Client:</div>
                                                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>{timesheet.clientComment || timesheet.comment}</div>
                                                    </div>
                                                  )}
                                                  {timesheet.employerComment && (
                                                    <div style={{ flex: 1, minWidth: 180, padding: "8px 12px", background: "#fff", border: "1px solid #EDE9FE", borderRadius: 8, fontSize: "0.78rem", color: "#1E293B" }}>
                                                      <div style={{ fontWeight: 700, color: "#7C3AED", fontSize: "0.72rem", marginBottom: 2 }}>Note for Employer:</div>
                                                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>{timesheet.employerComment}</div>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </td></tr>
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                          {/* Pagination */}
                          {totalPages > 1 && (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #E2EEFF", fontSize: "0.8rem", color: "#64748B" }}>
                              <span>Page {currentApprovalsPage} of {totalPages}</span>
                              <div style={{ display: "flex", gap: 6 }}>
                                <button disabled={currentApprovalsPage <= 1} onClick={() => { setApprovalsPage(currentApprovalsPage - 1); setExpandedTsId(null); }}
                                  style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #E2EEFF", background: currentApprovalsPage <= 1 ? "#F8FAFC" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: currentApprovalsPage <= 1 ? "#CBD5E1" : "#2563EB", cursor: currentApprovalsPage <= 1 ? "not-allowed" : "pointer" }}>
                                  ‹ Previous
                                </button>
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                  const page = i + 1;
                                  return <button key={page} onClick={() => { setApprovalsPage(page); setExpandedTsId(null); }}
                                    style={{ padding: "6px 10px", borderRadius: 8, border: page === currentApprovalsPage ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF", background: page === currentApprovalsPage ? "#EFF6FF" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: page === currentApprovalsPage ? "#2563EB" : "#64748B", cursor: "pointer", minWidth: 32 }}>
                                    {page}
                                  </button>;
                                })}
                                <button disabled={currentApprovalsPage >= totalPages} onClick={() => { setApprovalsPage(currentApprovalsPage + 1); setExpandedTsId(null); }}
                                  style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #E2EEFF", background: currentApprovalsPage >= totalPages ? "#F8FAFC" : "#fff", fontSize: "0.8rem", fontWeight: 600, color: currentApprovalsPage >= totalPages ? "#CBD5E1" : "#2563EB", cursor: currentApprovalsPage >= totalPages ? "not-allowed" : "pointer" }}>
                                  Next ›
                                </button>
                              </div>
                            </div>
                          )}
                        </>))}

                      {/* Employee Timesheets Accordion Header */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 16,
                          padding: "12px 20px",
                          background: "#F8FBFF",
                          borderBottom: "1px solid #E2EEFF",
                          cursor: "pointer",
                          userSelect: "none",
                          borderTop: "1px solid #E2EEFF"
                        }}
                        onClick={() => setShowEmployeeTimesheets(!showEmployeeTimesheets)}
                      >
                        <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A" }}>
                          {showEmployeeTimesheets ? "▼" : "▶"} Employee Timesheets
                        </span>
                        <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "5px 12px",
                              borderRadius: 8,
                              border: "1.5px solid #E2EEFF",
                              background: "#fff",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: "#0F172A",
                              cursor: "pointer",
                              outline: "none",
                              transition: "all 0.15s"
                            }}
                          >
                            <span>{selectedFilterClient === "ALL" ? "All Clients" : selectedFilterClient}</span>
                            <span style={{ fontSize: "0.6rem", color: "#64748B", display: "inline-block", transition: "transform 0.15s", transform: isFilterDropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                          </button>

                          {isFilterDropdownOpen && (
                            <div
                              style={{
                                position: "absolute",
                                top: "calc(100% + 6px)",
                                left: 0,
                                background: "#fff",
                                border: "1px solid #E2EEFF",
                                borderRadius: 8,
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                                zIndex: 100,
                                minWidth: 130,
                                padding: "4px 0",
                                display: "flex",
                                flexDirection: "column"
                              }}
                            >
                              <button
                                onClick={() => { setSelectedFilterClient("ALL"); setIsFilterDropdownOpen(false); }}
                                className={`em-dropdown-item ${selectedFilterClient === "ALL" ? "active" : ""}`}
                              >
                                All Clients
                              </button>
                              <button
                                onClick={() => { setSelectedFilterClient("Internal"); setIsFilterDropdownOpen(false); }}
                                className={`em-dropdown-item ${selectedFilterClient === "Internal" ? "active" : ""}`}
                              >
                                Internal
                              </button>
                              {activeClients.map(c => (
                                <button
                                  key={c.username}
                                  onClick={() => { setSelectedFilterClient(c.fullName); setIsFilterDropdownOpen(false); }}
                                  className={`em-dropdown-item ${selectedFilterClient === c.fullName ? "active" : ""}`}
                                >
                                  {c.fullName}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {showEmployeeTimesheets && (
                        <div className="em-card-scroll">
                          <table className="em-table">
                            <thead><tr><th>Employee</th><th>Username</th><th>Client</th><th>Total Timesheets</th><th>Action</th></tr></thead>
                            <tbody>
                              {activeEmployees.filter(emp => {
                                const empClient = emp.clientName || "Internal";
                                if (selectedFilterClient === "ALL") return true;
                                return empClient === selectedFilterClient;
                              }).map(emp => {
                                const empRows = allRows.filter(r => r.employee.username === emp.username).sort((a, b) => new Date(b.timesheet.week_start) - new Date(a.timesheet.week_start));
                                const isOpen = selectedEmp?.username === emp.username;
                                return (
                                  <React.Fragment key={emp.username}>
                                    <tr style={{ background: isOpen ? "#EFF6FF" : undefined }}>
                                      <td><div className="em-emp-cell" style={{ cursor: "pointer" }} onClick={() => setSelectedEmp(isOpen ? null : emp)}><div className="em-emp-avatar">{(emp.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name" style={{ color: "#2563EB" }}>{emp.fullName}</div></div></div></td>
                                      <td className="em-date">{emp.username}</td>
                                      <td style={{ fontSize: "0.8rem", color: "#475569" }}>{emp.clientName || "Internal"}</td>
                                      <td><span className="em-hours-badge">{empRows.length} Timesheet{empRows.length !== 1 ? "s" : ""}</span></td>
                                      <td>
                                        <div className="em-actions">
                                          <button className="em-action-btn view" title="View all timesheets" onClick={() => setSelectedEmp(isOpen ? null : emp)}><Icon.Eye size={12} color="currentColor" /></button>
                                        </div>
                                      </td>
                                    </tr>
                                    {isOpen && (
                                      <tr><td colSpan={5} style={{ padding: "16px 20px", background: "#F8FBFF", borderTop: "1px solid #E2EEFF" }}>
                                        <h4 style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A", marginBottom: 10 }}>
                                          Timesheets for {emp.fullName} ({empRows.length})
                                        </h4>
                                        {(() => {
                                          const monthMap = {};
                                          empRows.forEach(r => {
                                            const mLabel = dayjs(r.timesheet.week_start).format("MMMM YYYY");
                                            if (!monthMap[mLabel]) {
                                              monthMap[mLabel] = {
                                                monthLabel: mLabel,
                                                totalHours: 0,
                                                rows: []
                                              };
                                            }
                                            monthMap[mLabel].totalHours += Number(r.timesheet.total_hours || 0);
                                            monthMap[mLabel].rows.push(r);
                                          });
                                          const sortedMonths = Object.keys(monthMap).sort((a, b) => new Date(b) - new Date(a));

                                          return (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                              {sortedMonths.map(mLabel => {
                                                const mData = monthMap[mLabel];
                                                const isMonthOpen = !!expandedMonths[emp.username + "_" + mLabel];
                                                return (
                                                  <div key={mLabel} style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 8, overflow: "hidden" }}>
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#F8FBFF", cursor: "pointer", borderBottom: isMonthOpen ? "1px solid #E2EEFF" : "none" }}
                                                      onClick={() => setExpandedMonths(isMonthOpen ? {} : { [emp.username + "_" + mLabel]: true })}>
                                                      <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#334155" }}>{mLabel} <span style={{ fontWeight: 500, color: "#64748B", marginLeft: 8 }}>({mData.totalHours}h)</span></span>
                                                      <span style={{ fontSize: "0.75rem", color: "#2563EB", fontWeight: 600 }}>{isMonthOpen ? "Hide" : "Show"}</span>
                                                    </div>
                                                    {isMonthOpen && (
                                                      <table style={{ width: "100%", fontSize: "0.76rem", borderCollapse: "collapse" }}>
                                                        <thead>
                                                          <tr style={{ background: "#F8FBFF", borderBottom: "1px solid #E2EEFF", color: "#64748B", fontWeight: 700 }}>
                                                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Week Start – Week End</th>
                                                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Total Hours</th>
                                                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Status</th>
                                                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Daily Breakdown</th>
                                                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Action</th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {mData.rows.map(({ timesheet: ts }) => (
                                                            <tr key={ts.id} style={{ borderTop: "1px solid #F1F5F9" }}>
                                                              <td style={{ padding: "6px 8px" }}>{dayjs(ts.week_start).format("MMM D")} – {dayjs(ts.week_end).format("MMM D")}</td>
                                                              <td style={{ padding: "6px 8px", fontWeight: 600 }}>{ts.total_hours}h</td>
                                                              <td style={{ padding: "6px 8px" }}><span className={`em-status-pill ${ts.status?.toLowerCase().replace(/\s+/g, "-")}`}>{ts.status}</span></td>
                                                              <td style={{ padding: "6px 8px" }}>
                                                                <div style={{ display: "flex", gap: 6 }}>
                                                                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                                                                    <div key={d} style={{ textAlign: "center", minWidth: 32 }}>
                                                                      <div style={{ fontWeight: 600, color: "#64748B", fontSize: 0.7 }}>{d}</div>
                                                                      <div style={{ fontWeight: 500, color: "#94A3B8", fontSize: "0.68rem" }}>{dayjs(ts.week_start).add(i, "day").format("M/D")}</div>
                                                                      <div style={{ fontWeight: 600, color: "#0F172A" }}>{ts.daily_hours?.[d.toLowerCase().slice(0, 3)] ?? 0}h</div>
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              </td>
                                                              <td style={{ padding: "6px 8px" }}>
                                                                {(ts.status === "Approved" || ts.status === "Rejected") && (
                                                                  <button
                                                                    className="em-action-btn view"
                                                                    title="Revert to Pending"
                                                                    onClick={() => updateTimesheetStatus(ts.id, "Pending")}
                                                                    style={{ fontSize: "0.72rem", padding: "4px 8px", width: "auto", fontWeight: 600, whiteSpace: "nowrap" }}
                                                                  >
                                                                    ↩ Revert
                                                                  </button>
                                                                )}
                                                              </td>
                                                            </tr>
                                                          ))}
                                                        </tbody>
                                                      </table>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        })()}
                                      </td></tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {approvalsTab === "monthly" && (
                    <>
                      {/* Employee Hours Calculator */}
                      <div style={{ padding: "16px 20px", borderBottom: "1px solid #E2EEFF", background: "#fff" }}>
                        <h4 style={{ fontSize: "0.875rem", fontWeight: 700, color: "#0F172A", marginBottom: 12 }}>Employee Hours Calculator</h4>
                        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase" }}>From Date</label>
                            <input type="date" value={calcFrom} onChange={e => setCalcFrom(e.target.value)}
                              style={{ padding: "7px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", background: "#F8FBFF", outline: "none" }} />
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase" }}>To Date</label>
                            <input type="date" value={calcTo} onChange={e => setCalcTo(e.target.value)}
                              style={{ padding: "7px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", background: "#F8FBFF", outline: "none" }} />
                          </div>
                          {(calcFrom || calcTo) && (
                            <button onClick={() => { setCalcFrom(""); setCalcTo(""); }}
                              style={{ padding: "7px 14px", borderRadius: 8, border: "1.5px solid #FECACA", background: "#FEF2F2", fontSize: "0.8rem", fontWeight: 600, color: "#DC2626", cursor: "pointer" }}>
                              Clear
                            </button>
                          )}
                        </div>
                        {calcFrom && calcTo && (() => {
                          const rangeEmpData = activeEmployees.map(emp => {
                            const empRows = allRows.filter(r => r.employee.username === emp.username);
                            const rangeRows = empRows
                              .filter(r => {
                                const ws = r.timesheet?.week_start;
                                const we = r.timesheet?.week_end || (ws ? dayjs(ws).add(6, "day").format("YYYY-MM-DD") : null);
                                return ws && we && ws <= calcTo && we >= calcFrom;
                              })
                              .map(r => {
                                const inRangeHours = getTimesheetHoursInRange(r.timesheet, calcFrom, calcTo);
                                return {
                                  ...r,
                                  inRangeHours,
                                };
                              });
                            const totalHrs = rangeRows.reduce((s, r) => s + r.inRangeHours, 0);
                            const approvedHrs = rangeRows.filter(r => r.timesheet?.status === "Approved").reduce((s, r) => s + r.inRangeHours, 0);
                            const pendingHrs = rangeRows.filter(r => r.timesheet?.status === "Pending" || r.timesheet?.status === "Submitted").reduce((s, r) => s + r.inRangeHours, 0);
                            const rejectedHrs = rangeRows.filter(r => r.timesheet?.status === "Rejected").reduce((s, r) => s + r.inRangeHours, 0);
                            return { ...emp, totalHrs, approvedHrs, pendingHrs, rejectedHrs, weeks: rangeRows.length, rangeRows };
                          });
                          return (
                            <div style={{ marginTop: 14 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                                <span style={{ fontSize: "0.8rem", color: "#64748B", fontWeight: 500 }}>{dayjs(calcFrom).format("MMM D, YYYY")} – {dayjs(calcTo).format("MMM D, YYYY")}</span>
                              </div>
                              <div className="em-card-scroll">
                                <table className="em-table">
                                  <thead><tr><th>Employee</th><th>Total Hours</th><th>Approved</th><th>Pending</th><th>Rejected</th><th>Weeks</th><th>Action</th></tr></thead>
                                  <tbody>
                                    {rangeEmpData.map(e => (
                                      <React.Fragment key={e.username}>
                                        <tr style={{ background: expandedRangeEmp === e.username ? "#EFF6FF" : undefined }}>
                                          <td><div className="em-emp-cell"><div className="em-emp-avatar">{(e.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{e.fullName}</div></div></div></td>
                                          <td><span className="em-hours-badge">{e.totalHrs}h</span></td>
                                          <td style={{ color: "#10B981", fontWeight: 600 }}>{e.approvedHrs}h</td>
                                          <td style={{ color: "#F59E0B", fontWeight: 600 }}>{e.pendingHrs}h</td>
                                          <td style={{ color: "#EF4444", fontWeight: 600 }}>{e.rejectedHrs}h</td>
                                          <td style={{ fontSize: "0.8rem", color: "#64748B" }}>{e.weeks}</td>
                                          <td>
                                            <button className="em-action-btn view" title="View daily breakdown" onClick={() => setExpandedRangeEmp(expandedRangeEmp === e.username ? null : e.username)}>
                                              <Icon.Eye size={12} color="currentColor" />
                                            </button>
                                          </td>
                                        </tr>
                                        {expandedRangeEmp === e.username && (
                                          <tr>
                                            <td colSpan={7} style={{ padding: "12px 16px", background: "#F8FBFF", borderTop: "1px solid #E2EEFF" }}>
                                              {e.rangeRows.length === 0 ? (
                                                <p style={{ margin: 0, fontSize: "0.8rem", color: "#94A3B8" }}>No timesheets submitted in this period.</p>
                                              ) : (
                                                <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse" }}>
                                                  <thead>
                                                    <tr style={{ color: "#64748B", fontWeight: 700, borderBottom: "1px solid #E2EEFF" }}>
                                                      <th style={{ padding: "6px 8px", textAlign: "left" }}>Week Range</th>
                                                      <th style={{ padding: "6px 8px", textAlign: "left" }}>Hours</th>
                                                      <th style={{ padding: "6px 8px", textAlign: "left" }}>Status</th>
                                                      <th style={{ padding: "6px 8px", textAlign: "left" }}>Daily Breakdown</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {e.rangeRows.map(({ timesheet: ts, inRangeHours }) => (
                                                      <tr key={ts.id} style={{ borderTop: "1px solid #F1F5F9" }}>
                                                        <td style={{ padding: "6px 8px" }}>{dayjs(ts.week_start).format("MMM D")} – {dayjs(ts.week_end).format("MMM D")}</td>
                                                        <td style={{ padding: "6px 8px", fontWeight: 600 }}>
                                                          {inRangeHours}h
                                                          {inRangeHours !== Number(ts.total_hours) && (
                                                            <span style={{ fontSize: "0.7rem", color: "#94A3B8", fontWeight: 400, marginLeft: 4 }}>
                                                              ({ts.total_hours}h total)
                                                            </span>
                                                          )}
                                                        </td>
                                                        <td style={{ padding: "6px 8px" }}><span className={`em-status-pill ${ts.status?.toLowerCase().replace(/\s+/g, "-")}`}>{ts.status}</span></td>
                                                        <td style={{ padding: "6px 8px" }}>
                                                          <div style={{ display: "flex", gap: 6 }}>
                                                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => {
                                                              const dayDate = dayjs(ts.week_start).add(i, "day");
                                                              const dateStr = dayDate.format("YYYY-MM-DD");
                                                              const isInRange = (!calcFrom || dateStr >= calcFrom) && (!calcTo || dateStr <= calcTo);
                                                              return (
                                                                <div key={d} style={{ textAlign: "center", minWidth: 32, opacity: isInRange ? 1 : 0.35 }}>
                                                                  <div style={{ fontWeight: 600, color: isInRange ? "#64748B" : "#94A3B8", fontSize: "0.7rem" }}>{d}</div>
                                                                  <div style={{ fontWeight: 500, color: isInRange ? "#94A3B8" : "#CBD5E1", fontSize: "0.68rem" }}>{dayDate.format("M/D")}</div>
                                                                  <div style={{ fontWeight: 600, color: isInRange ? "#0F172A" : "#94A3B8" }}>{ts.daily_hours?.[d.toLowerCase().slice(0, 3)] ?? 0}h</div>
                                                                </div>
                                                              );
                                                            })}
                                                          </div>
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              )}
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", background: "#F8FBFF", borderBottom: "1px solid #E2EEFF" }}>
                        <h4 style={{ fontSize: "0.875rem", fontWeight: 700, color: "#0F172A" }}>{month.format("MMMM YYYY")}</h4>
                        <select
                          value={selectedMonthValue}
                          onChange={e => setSelectedMonth(e.target.value)}
                          style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", background: "#fff", cursor: "pointer", outline: "none" }}
                        >
                          {monthOptions.map(m => (
                            <option key={m.format("YYYY-MM")} value={m.format("YYYY-MM")}>{m.format("MMMM YYYY")}</option>
                          ))}
                        </select>
                      </div>
                      {empList.length === 0 ? (
                        <p style={{ padding: "16px 20px", fontSize: "0.8rem", color: "#94A3B8" }}>No timesheets for this month.</p>
                      ) : (
                        <div className="em-card-scroll">
                          <table className="em-table">
                            <thead><tr><th>Employee</th><th>Monthly Hours</th><th>Weeks Submitted</th><th>Action</th></tr></thead>
                            <tbody>
                              {empList.map(({ employee, totalHours, weeks }) => (
                                <React.Fragment key={employee.username}>
                                  <tr>
                                    <td><div className="em-emp-cell"><div className="em-emp-avatar">{(employee.fullName || "?")[0].toUpperCase()}</div><div><div className="em-emp-name">{employee.fullName}</div></div></div></td>
                                    <td><span className="em-hours-badge">{totalHours}h 00m</span></td>
                                    <td>{weeks.length} week{weeks.length !== 1 ? "s" : ""}</td>
                                    <td>
                                      <button className="em-action-btn view" title="View weekly breakdown" onClick={() => setExpandedTsId(expandedTsId === employee.username + monthStart ? null : employee.username + monthStart)}><Icon.Eye size={12} color="currentColor" /></button>
                                    </td>
                                  </tr>
                                  {expandedTsId === employee.username + monthStart && (
                                    <tr><td colSpan={4} style={{ padding: "14px 20px", background: "#F8FBFF", borderTop: "1px solid #E2EEFF" }}>
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, padding: "8px 12px", background: "#EFF6FF", borderRadius: 8 }}>
                                        <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.8rem" }}>Monthly Total for {employee.fullName}:</span>
                                        <span style={{ fontWeight: 700, color: "#2563EB", fontSize: "0.84rem" }}>{totalHours}h 00m</span>
                                      </div>
                                      <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse" }}>
                                        <thead><tr style={{ color: "#64748B", fontWeight: 700 }}><th style={{ padding: "6px 8px", textAlign: "left" }}>Week Range</th><th style={{ padding: "6px 8px", textAlign: "left" }}>Hours</th><th style={{ padding: "6px 8px", textAlign: "left" }}>Status</th><th style={{ padding: "6px 8px", textAlign: "left" }}>Daily Breakdown</th></tr></thead>
                                        <tbody>
                                          {weeks.map(ts => (
                                            <tr key={ts.id} style={{ borderTop: "1px solid #E2EEFF" }}>
                                              <td style={{ padding: "6px 8px" }}>{dayjs(ts.week_start).format("MMM D")} – {dayjs(ts.week_end).format("MMM D")}</td>
                                              <td style={{ padding: "6px 8px", fontWeight: 600 }}>
                                                {ts.monthHours ?? ts.total_hours}h
                                                {(ts.monthHours !== undefined && ts.monthHours !== Number(ts.total_hours)) && (
                                                  <span style={{ fontSize: "0.7rem", color: "#94A3B8", fontWeight: 400, marginLeft: 4 }}>
                                                    ({ts.total_hours}h total)
                                                  </span>
                                                )}
                                              </td>
                                              <td style={{ padding: "6px 8px" }}><span className={`em-status-pill ${ts.status?.toLowerCase().replace(/\s+/g, "-")}`}>{ts.status}</span></td>
                                              <td style={{ padding: "6px 8px" }}>
                                                <div style={{ display: "flex", gap: 6 }}>
                                                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => {
                                                    const dayDate = dayjs(ts.week_start).add(i, "day");
                                                    const dateStr = dayDate.format("YYYY-MM-DD");
                                                    const isInMonth = dateStr >= monthStart && dateStr <= monthEnd;
                                                    return (
                                                      <div key={d} style={{ textAlign: "center", minWidth: 32, opacity: isInMonth ? 1 : 0.35 }}>
                                                        <div style={{ fontWeight: 600, color: isInMonth ? "#64748B" : "#94A3B8", fontSize: "0.7rem" }}>{d}</div>
                                                        <div style={{ fontWeight: 500, color: isInMonth ? "#94A3B8" : "#CBD5E1", fontSize: "0.68rem" }}>{dayDate.format("M/D")}</div>
                                                        <div style={{ fontWeight: 600, color: isInMonth ? "#0F172A" : "#94A3B8" }}>{ts.daily_hours?.[d.toLowerCase().slice(0, 3)] ?? 0}h</div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td></tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            );
          })()}

          {/* TARGETS & APPRAISALS VIEW */}
          {activeNav === "appraisals" && (
            <TargetsAppraisalsView activeEmployees={activeEmployees} clients={activeClients} />
          )}

          {/* HOLIDAYS VIEW */}
          {activeNav === "holidays" && (
            <HolidayAdminView />
          )}

          {/* REPORTS VIEW */}
          {activeNav === "reports" && (() => {
            const now = dayjs();
            // Monthly breakdown by status (last 24 months)
            const monthlyStatusData = [];
            for (let i = 23; i >= 0; i--) {
              const m = now.subtract(i, "month").startOf("month");
              const mStart = m.format("YYYY-MM-DD");
              const mEnd = m.endOf("month").format("YYYY-MM-DD");
              const monthRows = allRows.filter(r => {
                const ws = r.timesheet?.week_start;
                const we = r.timesheet?.week_end || (ws ? dayjs(ws).add(6, "day").format("YYYY-MM-DD") : null);
                return ws && we && ws <= mEnd && we >= mStart;
              });
              const getHoursInMonthForStatus = (statusList) => {
                let total = 0;
                monthRows.filter(r => statusList.includes(r.timesheet?.status)).forEach(({ timesheet }) => {
                  if (!timesheet) return;
                  const start = dayjs(timesheet.week_start);
                  const daysOfWeek = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
                  daysOfWeek.forEach((day, index) => {
                    const dayDate = start.add(index, "day");
                    const dateStr = dayDate.format("YYYY-MM-DD");
                    if (dateStr >= mStart && dateStr <= mEnd) {
                      total += Number(timesheet.daily_hours?.[day] || 0);
                    }
                  });
                });
                return total;
              };
              monthlyStatusData.push({
                label: m.format("MMM 'YY"),
                fullLabel: m.format("MMMM YYYY"),
                approvedHrs: getHoursInMonthForStatus(["Approved"]),
                pendingHrs: getHoursInMonthForStatus(["Pending", "Submitted"]),
                rejectedHrs: getHoursInMonthForStatus(["Rejected"]),
                approvedCount: monthRows.filter(r => r.timesheet?.status === "Approved").length,
                pendingCount: monthRows.filter(r => r.timesheet?.status === "Pending" || r.timesheet?.status === "Submitted").length,
                rejectedCount: monthRows.filter(r => r.timesheet?.status === "Rejected").length,
              });
            }
            // Only show months that have data
            const activeMonthlyData = monthlyStatusData.filter(d => (d.approvedHrs + d.pendingHrs + d.rejectedHrs) > 0);
            const displayData = activeMonthlyData.length > 0 ? activeMonthlyData : monthlyStatusData.slice(-6);
            const maxMonthHrs = Math.max(...displayData.map(d => d.approvedHrs + d.pendingHrs + d.rejectedHrs), 1);

            // Per-employee summary
            const empColors = ["#2563EB", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#14B8A6"];
            const empSummary = activeEmployees.map((emp, i) => {
              const empRows = allRows.filter(r => r.employee.username === emp.username);
              return {
                name: emp.fullName,
                totalHours: empRows.reduce((s, r) => s + Number(r.timesheet?.total_hours || 0), 0),
                approved: empRows.filter(r => r.timesheet?.status === "Approved").length,
                pending: empRows.filter(r => r.timesheet?.status === "Pending" || r.timesheet?.status === "Submitted").length,
                rejected: empRows.filter(r => r.timesheet?.status === "Rejected").length,
                color: empColors[i % empColors.length],
              };
            }).sort((a, b) => b.totalHours - a.totalHours);

            // Overall totals
            const totalApproved = allRows.filter(r => r.timesheet?.status === "Approved").length;
            const totalPending = allRows.filter(r => r.timesheet?.status === "Pending" || r.timesheet?.status === "Submitted").length;
            const totalRejected = allRows.filter(r => r.timesheet?.status === "Rejected").length;
            const totalAllHrs = allRows.reduce((s, r) => s + Number(r.timesheet?.total_hours || 0), 0);

            return (
              <div className="em-card">
                <div className="em-card-header"><h3>Reports</h3></div>

                {/* Summary Cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, padding: "20px" }}>
                  <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#065F46" }}>{totalApproved}</div>
                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#047857" }}>Timesheets Approved</div>
                  </div>
                  <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#92400E" }}>{totalPending}</div>
                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#B45309" }}>Awaiting Approval</div>
                  </div>
                  <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#991B1B" }}>{totalRejected}</div>
                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#DC2626" }}>Timesheets Rejected</div>
                  </div>
                  <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#1E40AF" }}>{totalAllHrs}h</div>
                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#2563EB" }}>Total Hours Logged</div>
                  </div>
                </div>

                {/* Monthly Bar Chart with tooltip-like labels */}
                <div style={{ padding: "24px 20px", borderTop: "1px solid #E2EEFF" }}>
                  <h4 style={{ fontSize: "0.875rem", fontWeight: 700, color: "#0F172A", marginBottom: 12 }}>Monthly Hours by Status</h4>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14, fontSize: "0.72rem" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "#10B981", display: "inline-block" }} /> Approved</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "#F59E0B", display: "inline-block" }} /> Pending</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "#EF4444", display: "inline-block" }} /> Rejected</span>
                  </div>
                  <div style={{ overflowX: "auto", paddingBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 200, minWidth: displayData.length * 52 }}>
                      {displayData.map((d, i) => {
                        const total = d.approvedHrs + d.pendingHrs + d.rejectedHrs;
                        const barH = (total / maxMonthHrs) * 160;
                        const approvedH = total > 0 ? (d.approvedHrs / total) * barH : 0;
                        const pendingH = total > 0 ? (d.pendingHrs / total) * barH : 0;
                        const rejectedH = total > 0 ? (d.rejectedHrs / total) * barH : 0;
                        return (
                          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 44 }} title={`${d.fullLabel}\nApproved: ${d.approvedCount} (${d.approvedHrs}h)\nPending: ${d.pendingCount} (${d.pendingHrs}h)\nRejected: ${d.rejectedCount} (${d.rejectedHrs}h)`}>
                            <span style={{ fontSize: "0.65rem", fontWeight: 600, color: "#0F172A" }}>{total}h</span>
                            <div style={{ width: 32, display: "flex", flexDirection: "column-reverse", borderRadius: "5px 5px 0 0", overflow: "hidden", minHeight: 4 }}>
                              <div style={{ height: approvedH, background: "#10B981", transition: "height 0.3s" }} />
                              <div style={{ height: pendingH, background: "#F59E0B", transition: "height 0.3s" }} />
                              <div style={{ height: rejectedH, background: "#EF4444", transition: "height 0.3s" }} />
                            </div>
                            <span style={{ fontSize: "0.62rem", fontWeight: 600, color: "#64748B", whiteSpace: "nowrap" }}>{d.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Employee Summary Table */}
                <div style={{ padding: "24px 20px", borderTop: "1px solid #E2EEFF" }}>
                  <h4 style={{ fontSize: "0.875rem", fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Employee Timesheet Summary</h4>
                  <p style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: 14 }}>Total hours and approval status breakdown per employee.</p>
                  <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #E2EEFF" }}>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: "#64748B", fontSize: "0.72rem", textTransform: "uppercase" }}>Employee</th>
                        <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#64748B", fontSize: "0.72rem" }}>Total Hours</th>
                        <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#10B981", fontSize: "0.72rem" }}>Approved</th>
                        <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#F59E0B", fontSize: "0.72rem" }}>Pending</th>
                        <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#EF4444", fontSize: "0.72rem" }}>Rejected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empSummary.map((e, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                          <td style={{ padding: "10px", fontWeight: 600, color: "#1E293B" }}>{e.name}</td>
                          <td style={{ padding: "10px", textAlign: "center", fontWeight: 700, color: "#0F172A" }}>{e.totalHours}h</td>
                          <td style={{ padding: "10px", textAlign: "center", fontWeight: 600, color: "#10B981" }}>{e.approved}</td>
                          <td style={{ padding: "10px", textAlign: "center", fontWeight: 600, color: "#F59E0B" }}>{e.pending}</td>
                          <td style={{ padding: "10px", textAlign: "center", fontWeight: 600, color: "#EF4444" }}>{e.rejected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* NOTIFICATIONS VIEW */}
          {activeNav === "notifications" && (
            <div className="em-card">
              <div className="em-card-header">
                <h3>All Notifications</h3>
                {notifications.length > 0 && (
                  <button className="em-view-all" onClick={markAllRead}>Mark all read</button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="em-empty"><Icon.CheckCircle size={28} color="#A7F3D0" /><p>You're all caught up!</p></div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 20 }}>
                  {notifications.map(n => (
                    <div key={n.id} style={{ display: "flex", gap: 10, padding: "10px 14px", background: "#F8FBFF", borderRadius: 10, borderLeft: `3px solid ${n.type?.includes("APPROVED") ? "#10B981" : n.type?.includes("LEAVE") ? "#F59E0B" : "#2563EB"}` }}>
                      <div style={{ flex: 1, fontSize: "0.8125rem", color: "#1E293B", fontWeight: 500 }}>{n.message}</div>
                      <div style={{ fontSize: "0.72rem", color: "#94A3B8", whiteSpace: "nowrap" }}>{n.createdAt ? dayjs(n.createdAt).format("MMM D, YYYY") : ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FEEDBACK VIEW */}
          {activeNav === "feedback" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyItems: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div style={{ fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>AI Feedback Hub</div>
              </div>
              <EmployerFeedbackView />
            </div>
          )}

          {/* SETTINGS VIEW */}
          {activeNav === "settings" && (
            <div className="em-card" style={{ padding: 24 }}>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: 20 }}>Settings</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[{ label: "Name", value: name || "—" }, { label: "Username", value: username || "—" }, { label: "Role", value: "Employer" }, { label: "Team Size", value: `${totalEmp} employees` }].map(f => (
                  <div key={f.label}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 5 }}>{f.label}</label>
                    <div style={{ padding: "10px 14px", background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: "0.875rem", fontWeight: 500, color: "#0F172A" }}>{f.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT PANEL (dashboard only) */}
        {activeNav === "dashboard" && (
          <div className="em-right">
            {/* This Week Overview */}
            <motion.div className="em-progress-card" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.3 }}>
              <div className="em-panel-header">
                <span className="em-panel-title">This Week Overview</span>
                <span className="em-panel-date">{dayjs(weekStart).format("MMM D")} – {dayjs(weekEnd).format("MMM D, YYYY")}</span>
              </div>
              <div className="em-progress-circle-wrap">
                <CircProg pct={weekPct} />
                <div className="em-progress-info">
                  <h4>{totalHrsThisWeek}h</h4>
                  <p>Total Hours Logged</p>
                  <div className="em-progress-sub">{submitted}<span>/{totalEmp}</span></div>
                  <p style={{ fontSize: "0.68rem", color: "#64748B" }}>Timesheets Submitted</p>
                </div>
              </div>
              <button className="em-view-analytics" onClick={() => setActiveNav("reports")}>View Full Analytics →</button>
            </motion.div>

            {/* Timesheet Approvals summary */}
            <motion.div className="em-right-card" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25, duration: 0.3 }}>
              <div className="em-panel-header">
                <span className="em-panel-title">Timesheet Management</span>
                <button className="em-view-all" onClick={() => setActiveNav("approvals")}>View All</button>
              </div>
              <div className="em-approval-stats">
                {[
                  { label: "Pending", value: pendingTimesheets.length, color: "#F59E0B" },
                  { label: "Approved", value: approvedCount, color: "#10B981" },
                  { label: "Rejected", value: rejectedCount, color: "#EF4444" },
                  { label: "Not Submitted", value: notSubmitted, color: "#94A3B8" },
                ].map(s => (
                  <div key={s.label} className="em-approval-stat-row">
                    <span className="em-approval-dot" style={{ background: s.color }} />
                    <span className="em-approval-label">{s.label}</span>
                    <span className="em-approval-value">{s.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div className="em-right-card" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3, duration: 0.3 }}>
              <div className="em-panel-header">
                <span className="em-panel-title">Recent Activity</span>
              </div>
              <div className="em-activity-list">
                {recentActivity.length === 0 ? (
                  <p style={{ fontSize: "0.78rem", color: "#94A3B8" }}>No recent activity.</p>
                ) : recentActivity.map((a, i) => (
                  <div key={i} className="em-activity-item">
                    <div className={`em-activity-icon ${a.icon}`}>
                      {a.icon === "green" && <Icon.CheckCircle size={13} color="#10B981" />}
                      {a.icon === "blue" && <Icon.FileText size={13} color="#2563EB" />}
                      {a.icon === "amber" && <Icon.Calendar size={13} color="#F59E0B" />}
                    </div>
                    <div className="em-activity-text"><p>{a.text}</p><span>{a.time}</span></div>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        )}
      </div>

      {/* Support Modal */}
      <AnimatePresence>
        {showSupport && (
          <motion.div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowSupport(false)}>
            <motion.div style={{ background: "#fff", borderRadius: 18, boxShadow: "0 16px 48px rgba(37,99,235,0.15)", width: "100%", maxWidth: 420, overflow: "hidden" }}
              initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 20 }} transition={{ duration: 0.25 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", padding: "22px 24px", position: "relative" }}>
                <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: 4 }}>Contact Support</h2>
                <p style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.72)" }}>We're here to help</p>
                <button style={{ position: "absolute", top: 14, right: 14, width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.18)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => setShowSupport(false)}><Icon.X size={11} color="#fff" /></button>
              </div>
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ padding: "10px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10 }}>
                  <p style={{ fontSize: "0.8rem", fontWeight: 600, color: "#1E3A8A", marginBottom: 2 }}>📧 Email Support</p>
                  <p style={{ fontSize: "0.8rem", color: "#2563EB" }}>support@skypa.com</p>
                </div>
                <button style={{ width: "100%", padding: "11px", background: "#2563EB", color: "#fff", border: "none", borderRadius: 10, fontSize: "0.9rem", fontWeight: 700, cursor: "pointer" }}
                  onClick={() => { alert("Our team will respond within 24 hours."); setShowSupport(false); }}>
                  Send Message
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── REJECTION REASON MODAL ── */}
      <AnimatePresence>
        {rejectModal && (
          <motion.div
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: 20 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setRejectModal(null)}
          >
            <motion.div
              style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 16, boxShadow: "0 16px 48px rgba(37,99,235,0.14)", width: "100%", maxWidth: 440, overflow: "hidden" }}
              initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 16 }} transition={{ duration: 0.22 }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ background: "linear-gradient(135deg,#7f1d1d,#dc2626)", padding: "22px 24px", position: "relative" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#fff", marginBottom: 4 }}>Reject Leave Request</h3>
                <p style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.75)" }}>Please provide a reason for rejection</p>
                <button
                  style={{ position: "absolute", top: 14, right: 14, width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer", color: "#fff", fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => setRejectModal(null)} aria-label="Close"
                >×</button>
              </div>
              <div style={{ padding: "24px" }}>
                <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "#1E293B", display: "block", marginBottom: 6 }}>
                  Rejection Reason <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  rows={4}
                  placeholder="Explain why this leave request is being rejected..."
                  style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${rejectReason.trim() ? "#E2EEFF" : "#FCA5A5"}`, background: rejectReason.trim() ? "#fff" : "#FFF8F8", borderRadius: 8, fontSize: "0.84rem", fontFamily: "inherit", outline: "none", resize: "vertical", minHeight: 100, boxSizing: "border-box" }}
                  autoFocus
                />
                {!rejectReason.trim() && (
                  <p style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: 5, fontWeight: 500 }}>Reason is required to reject a leave request.</p>
                )}
                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button
                    onClick={() => setRejectModal(null)}
                    style={{ flex: 1, padding: "10px", background: "#F8FBFF", border: "1px solid #E2EEFF", borderRadius: 8, fontSize: "0.875rem", fontWeight: 600, color: "#64748B", cursor: "pointer", fontFamily: "inherit" }}
                  >Cancel</button>
                  <button
                    onClick={handleRejectConfirm}
                    disabled={!rejectReason.trim()}
                    style={{ flex: 1, padding: "10px", background: rejectReason.trim() ? "#DC2626" : "#FCA5A5", border: "none", borderRadius: 8, fontSize: "0.875rem", fontWeight: 700, color: "#fff", cursor: rejectReason.trim() ? "pointer" : "not-allowed", fontFamily: "inherit", transition: "background 0.15s" }}
                  >Confirm Rejection</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ EMPLOYEE DETAIL MODAL ══ */}
      <AnimatePresence>
        {detailEmp && detailProfile && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={() => { setDetailEmp(null); setDetailProfile(null); setDetailEditing(false); setDetailMsg(null); }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              style={{ background: "#fff", borderRadius: 16, width: 540, maxHeight: "85vh", overflowY: "auto", padding: "28px 32px", boxShadow: "0 16px 48px rgba(37,99,235,0.18)" }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A" }}>Employee Details</h3>
                <button onClick={() => { setDetailEmp(null); setDetailProfile(null); setDetailEditing(false); setDetailMsg(null); }}
                  aria-label="Close"
                  style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #E2EEFF", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon.X size={14} color="#64748B" />
                </button>
              </div>

              {/* Avatar + Name header */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, padding: "14px 16px", background: "#F8FBFF", borderRadius: 12, border: "1px solid #E2EEFF" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#2563EB", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", fontWeight: 700 }}>
                  {(detailProfile.name || detailEmp.fullName || "?")[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A" }}>{detailProfile.name || detailEmp.fullName || "—"}</div>
                  <div style={{ fontSize: "0.78rem", color: "#64748B" }}>{detailProfile.username} · {detailProfile.role || "Employee"}</div>
                </div>
                <div style={{ marginLeft: "auto" }}>
                  <span style={{ padding: "4px 10px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 600, background: detailProfile.active ? "#ECFDF5" : "#FEF2F2", color: detailProfile.active ? "#059669" : "#DC2626" }}>
                    {detailProfile.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {/* Profile fields */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {(() => {
                  const empTz = ((detailEditing ? detailForm.timezone : detailProfile.timezone) || detailEmp.timezone || "America/New_York").toLowerCase();
                  const isAmericaTimezone = empTz.startsWith("america/") || empTz.includes("york") || empTz.includes("est") || empTz.includes("edt") || empTz.includes("cst") || empTz.includes("pst") || empTz.includes("us") || empTz.includes("america");
                  const currentWp = (detailEditing ? detailForm.workPermit : detailProfile.workPermit) || "Citizen";
                  const showExpiry = isAmericaTimezone && currentWp && currentWp.toLowerCase() !== "citizen";

                  const fields = [
                    { label: "Full Name", key: "name", fallback: detailEmp.fullName },
                    { label: "Username", key: "username", readOnly: true },
                    { label: "Email", key: "email" },
                    { label: "Start Date", key: "startDate", type: "date", fallback: detailEmp.startDate },
                    { label: "Date of Birth", key: "dateOfBirth", type: "date" },
                    { label: "Client", key: "clientName", fallback: detailEmp.clientName, isDropdown: true },
                    { label: "Timezone", key: "timezone", isTimezoneDropdown: true },
                    { label: "Role", key: "role", readOnly: true, fallback: "Employee" },
                    ...(isAmericaTimezone ? [
                      { label: "Work Permit", key: "workPermit", isWorkPermitDropdown: true },
                      ...(showExpiry ? [
                        { label: "Expiry Date", key: "workPermitExpiry", type: "date" }
                      ] : [])
                    ] : [])
                  ];

                  return fields.map(f => {
                    const displayValue = detailProfile[f.key] || f.fallback || "—";
                    return (
                      <div key={f.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>{f.label}</label>
                        {detailEditing && !f.readOnly ? (
                          f.isDropdown ? (
                            <select value={detailForm[f.key] || ""}
                              onChange={e => setDetailForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                              style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", fontWeight: 500, color: "#0F172A", background: "#fff", outline: "none", cursor: "pointer" }}>
                              <option value="Internal">Internal</option>
                              {activeClients.map(c => (
                                <option key={c.username} value={c.fullName}>{c.fullName}</option>
                              ))}
                            </select>
                          ) : f.isTimezoneDropdown ? (
                            <select value={detailForm.timezone || "America/New_York"}
                              onChange={e => setDetailForm(prev => ({ ...prev, timezone: e.target.value }))}
                              style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", fontWeight: 500, color: "#0F172A", background: "#fff", outline: "none", cursor: "pointer" }}>
                              <option value="America/New_York">America/New_York</option>
                              <option value="Asia/Kolkata">Asia/Kolkata</option>
                            </select>
                          ) : f.isWorkPermitDropdown ? (
                            <select value={detailForm.workPermit || "Citizen"}
                              onChange={e => {
                                const val = e.target.value;
                                setDetailForm(prev => ({
                                  ...prev,
                                  workPermit: val,
                                  workPermitExpiry: val.toLowerCase() === "citizen" ? "" : prev.workPermitExpiry
                                }));
                              }}
                              style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", fontWeight: 500, color: "#0F172A", background: "#fff", outline: "none", cursor: "pointer" }}>
                              <option value="Citizen">Citizen</option>
                              <option value="H1b">H1b</option>
                              <option value="GC">GC</option>
                              <option value="OPT">OPT</option>
                            </select>
                          ) : (
                            <input type={f.type || "text"} value={detailForm[f.key] || (f.key === "name" ? detailEmp.fullName : "") || ""}
                              onChange={e => setDetailForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                              style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", fontWeight: 500, color: "#0F172A", background: "#fff", outline: "none" }} />
                          )
                        ) : (
                          <div style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #E2E8F0", fontSize: "0.84rem", fontWeight: 500, color: "#0F172A", background: "#F8FAFC" }}>
                            {f.key === "startDate" && displayValue && displayValue !== "—" ? dayjs(displayValue).format("MMM D, YYYY") :
                             f.key === "dateOfBirth" && displayValue && displayValue !== "—" ? dayjs(displayValue).format("MMM D, YYYY") :
                             f.key === "workPermitExpiry" && displayValue && displayValue !== "—" ? dayjs(displayValue).format("MMM D, YYYY") :
                             displayValue}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Client Notes toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 18, padding: "14px 16px", borderRadius: 10, border: "1.5px solid #E2EEFF", background: "#F8FBFF" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A" }}>Client Notes</span>
                  <span style={{ fontSize: "0.74rem", color: "#64748B", maxWidth: 380 }}>
                    When enabled, this employee sees a separate Client Notes section, visible to the Client. Employer Notes stay private to you and the employee.
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!detailForm.clientNotesEnabled}
                  aria-label="Enable Client Notes for this employee"
                  disabled={!detailEditing}
                  onClick={() => detailEditing && setDetailForm(prev => ({ ...prev, clientNotesEnabled: !prev.clientNotesEnabled }))}
                  style={{
                    position: "relative", width: 46, height: 26, borderRadius: 999, border: "none", flexShrink: 0,
                    background: detailForm.clientNotesEnabled ? "#2563EB" : "#CBD5E1",
                    cursor: detailEditing ? "pointer" : "not-allowed", opacity: detailEditing ? 1 : 0.7, transition: "background 0.2s",
                  }}>
                  <span style={{
                    position: "absolute", top: 3, left: detailForm.clientNotesEnabled ? 23 : 3, width: 20, height: 20,
                    borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }} />
                </button>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid #E2EEFF", flexWrap: "wrap" }}>
                {!detailEditing ? (
                  <button onClick={() => setDetailEditing(true)}
                    style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "#2563EB", color: "#fff", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}>
                    Edit Details
                  </button>
                ) : (
                  <>
                    <button onClick={saveEmployeeDetail} disabled={detailSaving}
                      style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "#2563EB", color: "#fff", fontSize: "0.82rem", fontWeight: 600, cursor: detailSaving ? "not-allowed" : "pointer" }}>
                      {detailSaving ? "Saving..." : "Save Changes"}
                    </button>
                    <button onClick={() => { setDetailEditing(false); setDetailForm(detailProfile); setDetailMsg(null); }}
                      style={{ padding: "9px 20px", borderRadius: 8, border: "1.5px solid #E2EEFF", background: "#fff", color: "#64748B", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}>
                      Cancel
                    </button>
                  </>
                )}
                <button onClick={async () => {
                  await toggleActive(detailEmp.username);
                  setDetailProfile(prev => ({ ...prev, active: !prev.active }));
                }}
                  style={{ marginLeft: "auto", padding: "9px 16px", borderRadius: 8, border: `1.5px solid ${detailProfile.active ? "#FECACA" : "#A7F3D0"}`, background: detailProfile.active ? "#FEF2F2" : "#ECFDF5", color: detailProfile.active ? "#DC2626" : "#059669", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}>
                  {detailProfile.active ? "Deactivate" : "Activate"}
                </button>
              </div>
              {detailMsg && (
                <p style={{ marginTop: 10, fontSize: "0.8rem", fontWeight: 600, color: detailMsg.type === "success" ? "#059669" : "#DC2626" }}>{detailMsg.text}</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ ADD CLIENT / ADD EMPLOYEE MODAL ══ */}
      <AnimatePresence>
        {createType && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => { if (!createLoading) setCreateType(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 16 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 16 }}
              style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 50px rgba(15,23,42,0.22)", border: "1px solid #E2EEFF" }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #1E40AF, #2563EB)", padding: "20px 24px", color: "#fff", position: "relative", borderTopLeftRadius: 15, borderTopRightRadius: 15 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {createType === "client" ? <Icon.Briefcase size={20} color="#fff" /> : <Icon.Users size={20} color="#fff" />}
                  </div>
                  <div>
                    <h3 style={{ fontSize: "1.05rem", fontWeight: 700, margin: 0, color: "#fff" }}>
                      {createType === "client" ? "Add New Client" : "Add New Employee"}
                    </h3>
                    <p style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.8)", margin: "3px 0 0 0" }}>
                      Creates account & generates temporary credentials to share
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { if (!createLoading) setCreateType(null); }}
                  style={{ position: "absolute", top: 16, right: 16, width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.18)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleCreateUserSubmit} style={{ padding: "24px" }}>
                {createError && (
                  <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626", fontSize: "0.82rem", fontWeight: 500, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon.AlertCircle size={16} color="#DC2626" />
                    <span>{createError}</span>
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {/* Full Name */}
                  <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Full Name <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={createType === "client" ? "Enter client name" : "Enter employee name"}
                      value={createForm.fullName}
                      onChange={e => setCreateForm(prev => ({ ...prev, fullName: e.target.value }))}
                      style={{ padding: "10px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.86rem", color: "#0F172A", outline: "none" }}
                    />
                  </div>

                  {/* Username (Optional) */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Username <span style={{ fontSize: "0.7rem", color: "#94A3B8", fontWeight: 400 }}>(Optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Auto-generated if blank"
                      value={createForm.username}
                      onChange={e => setCreateForm(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, "") }))}
                      style={{ padding: "10px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.86rem", color: "#0F172A", outline: "none" }}
                    />
                  </div>

                  {/* Timezone */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>Timezone</label>
                    <select
                      value={createForm.timezone}
                      onChange={e => setCreateForm(prev => ({ ...prev, timezone: e.target.value }))}
                      style={{ padding: "10px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", color: "#0F172A", background: "#fff", outline: "none", cursor: "pointer" }}
                    >
                      {TIMEZONE_OPTIONS.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Employee-specific fields */}
                  {createType === "employee" && (
                    <>
                      {/* Client Assignment */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>Assign Client</label>
                        <select
                          value={createForm.clientName}
                          onChange={e => setCreateForm(prev => ({ ...prev, clientName: e.target.value }))}
                          style={{ padding: "10px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", color: "#0F172A", background: "#fff", outline: "none", cursor: "pointer" }}
                        >
                          <option value="Internal">Internal</option>
                          {activeClients.map(c => (
                            <option key={c.username} value={c.fullName}>{c.fullName}</option>
                          ))}
                        </select>
                      </div>

                      {/* Start Date */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>Start Date</label>
                        <input
                          type="date"
                          value={createForm.startDate}
                          onChange={e => setCreateForm(prev => ({ ...prev, startDate: e.target.value }))}
                          style={{ padding: "10px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", color: "#0F172A", outline: "none" }}
                        />
                      </div>

                      {/* Work Permit (America Timezones Only) */}
                      {isAmericaTz(createForm.timezone) && (
                        <>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>Work Permit</label>
                            <select
                              value={createForm.workPermit}
                              onChange={e => {
                                const val = e.target.value;
                                setCreateForm(prev => ({
                                  ...prev,
                                  workPermit: val,
                                  workPermitExpiry: val.toLowerCase() === "citizen" ? "" : prev.workPermitExpiry
                                }));
                              }}
                              style={{ padding: "10px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", color: "#0F172A", background: "#fff", outline: "none", cursor: "pointer" }}
                            >
                              <option value="Citizen">Citizen</option>
                              <option value="H1b">H1b</option>
                              <option value="GC">GC</option>
                              <option value="OPT">OPT</option>
                            </select>
                          </div>

                          {createForm.workPermit !== "Citizen" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                                Permit Expiry Date <span style={{ color: "#EF4444" }}>*</span>
                              </label>
                              <input
                                type="date"
                                required
                                value={createForm.workPermitExpiry}
                                onChange={e => setCreateForm(prev => ({ ...prev, workPermitExpiry: e.target.value }))}
                                style={{ padding: "10px 12px", borderRadius: 8, border: "1.5px solid #E2EEFF", fontSize: "0.84rem", color: "#0F172A", outline: "none" }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    disabled={createLoading}
                    onClick={() => setCreateType(null)}
                    style={{ padding: "10px 18px", borderRadius: 8, border: "1.5px solid #E2EEFF", background: "#fff", color: "#64748B", fontSize: "0.84rem", fontWeight: 600, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    style={{
                      padding: "10px 22px",
                      borderRadius: 8,
                      border: "none",
                      background: "#2563EB",
                      color: "#fff",
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      cursor: createLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      boxShadow: "0 4px 12px rgba(37,99,235,0.25)"
                    }}
                  >
                    {createLoading ? "Creating Account..." : (createType === "client" ? "Create Client" : "Create Employee")}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ TEMPORARY CREDENTIALS DIALOG ══ */}
      <AnimatePresence>
        {createdCredentials && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => setCreatedCredentials(null)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0, y: 20 }}
              style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 520, overflow: "hidden", boxShadow: "0 24px 60px rgba(15,23,42,0.28)", border: "1px solid #E2EEFF" }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #059669, #10B981)", padding: "22px 24px", color: "#fff", textAlign: "center", position: "relative" }}>
                <div style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px auto" }}>
                  <Icon.CheckCircle size={26} color="#fff" />
                </div>
                <h3 style={{ fontSize: "1.15rem", fontWeight: 700, margin: 0, color: "#fff" }}>
                  Account Created Successfully!
                </h3>
                <p style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.9)", margin: "4px 0 0 0" }}>
                  Temporary login credentials have been generated
                </p>
                <button
                  type="button"
                  onClick={() => setCreatedCredentials(null)}
                  style={{ position: "absolute", top: 14, right: 14, width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: "24px" }}>
                <div style={{ padding: "12px 14px", borderRadius: 10, background: "#EFF6FF", border: "1px solid #BFDBFE", fontSize: "0.8rem", color: "#1E40AF", lineHeight: 1.45, marginBottom: 20 }}>
                  <strong>Next step:</strong> Share these credentials with <strong>{createdCredentials.name}</strong> ({createdCredentials.role === "client_manager" ? "Client" : "Employee"}). On their first login, they will be prompted to set their permanent password and complete their profile (and can update their username if desired).
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* Username Card */}
                  <div style={{ background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#64748B", marginBottom: 2 }}>
                        Username
                      </div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: "#0F172A", fontFamily: "monospace", letterSpacing: "0.02em" }}>
                        {createdCredentials.username}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(createdCredentials.username, "user")}
                      style={{
                        padding: "7px 14px",
                        borderRadius: 8,
                        border: copiedField === "user" ? "1.5px solid #10B981" : "1.5px solid #CBD5E1",
                        background: copiedField === "user" ? "#ECFDF5" : "#fff",
                        color: copiedField === "user" ? "#059669" : "#334155",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        transition: "all 0.15s"
                      }}
                    >
                      {copiedField === "user" ? <><Icon.Check size={13} color="#059669" /> Copied!</> : <><Icon.Copy size={13} color="#64748B" /> Copy</>}
                    </button>
                  </div>

                  {/* Temporary Password Card */}
                  <div style={{ background: "#FFFBEB", border: "1.5px solid #FDE68A", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#B45309", marginBottom: 2 }}>
                        Temporary Password
                      </div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: "#92400E", fontFamily: "monospace", letterSpacing: "0.02em" }}>
                        {createdCredentials.temporaryPassword}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(createdCredentials.temporaryPassword, "pass")}
                      style={{
                        padding: "7px 14px",
                        borderRadius: 8,
                        border: copiedField === "pass" ? "1.5px solid #10B981" : "1.5px solid #FCD34D",
                        background: copiedField === "pass" ? "#ECFDF5" : "#fff",
                        color: copiedField === "pass" ? "#059669" : "#78350F",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        transition: "all 0.15s"
                      }}
                    >
                      {copiedField === "pass" ? <><Icon.Check size={13} color="#059669" /> Copied!</> : <><Icon.Copy size={13} color="#92400E" /> Copy</>}
                    </button>
                  </div>
                </div>

                {/* Copy All Button */}
                <div style={{ marginTop: 18 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const text = `Timesheet Portal Login Credentials:\nRole: ${createdCredentials.role === "client_manager" ? "Client" : "Employee"}\nName: ${createdCredentials.name}\nUsername: ${createdCredentials.username}\nTemporary Password: ${createdCredentials.temporaryPassword}\nLogin URL: ${window.location.origin}`;
                      copyToClipboard(text, "all");
                    }}
                    style={{
                      width: "100%",
                      padding: "11px",
                      borderRadius: 10,
                      border: copiedField === "all" ? "1.5px solid #10B981" : "1.5px solid #2563EB",
                      background: copiedField === "all" ? "#ECFDF5" : "#EFF6FF",
                      color: copiedField === "all" ? "#059669" : "#2563EB",
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 7,
                      transition: "all 0.15s"
                    }}
                  >
                    {copiedField === "all" ? <><Icon.Check size={15} color="#059669" /> All Credentials Copied to Clipboard!</> : <><Icon.Copy size={15} color="#2563EB" /> Copy Full Credentials Summary</>}
                  </button>
                </div>

                <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => setCreatedCredentials(null)}
                    style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: "#0F172A", color: "#fff", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployerHome;
